import * as THREE from 'three';
import { EffectComposer }   from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass }       from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass }  from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass }       from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass }       from 'three/addons/postprocessing/ShaderPass.js';
import filmGrainFrag        from '../../glsl/film_grain_frag.glsl';
import nvgFrag              from '../../glsl/nvg_frag.glsl';
import AssetManager          from './AssetManager.js';
import Aircraft              from '../aircraft/Aircraft.js';
import Controller            from '../input/Controller.js';
import ChaseCamera           from '../camera/ChaseCamera.js';
import CockpitCamera         from '../camera/CockpitCamera.js';
import CameraManager, { CameraMode } from '../camera/CameraManager.js';
import ActionCamera          from '../camera/ActionCamera.js';
import EnemyCamera           from '../camera/EnemyCamera.js';
import StartCamera           from '../camera/StartCamera.js';
import IntroCamera           from '../camera/IntroCamera.js';
import IntroSequence         from '../ui/IntroSequence.js';
import { shouldPlayIntro, markIntroSeen } from './FirstRun.js';
import DebugHUD              from '../ui/DebugHUD.js';
import GameHUD               from '../ui/GameHUD.js';
import CommandFeedback       from '../ui/CommandFeedback.js';
import ClickPrompt          from '../ui/ClickPrompt.js';
import OutOfBoundsWarning    from '../ui/OutOfBoundsWarning.js';
import DestroyedOverlay       from '../ui/DestroyedOverlay.js';
import MissionCompleteOverlay  from '../ui/MissionCompleteOverlay.js';
import MissionStats           from '../mission/MissionStats.js';
import JTACRadio             from '../mission/JTACRadio.js';
import * as MissionRecords    from '../mission/MissionRecords.js';
import TrainingMission        from '../training/TrainingMission.js';
import { getTrainingMission } from '../training/missions/index.js';
import ForceViz              from '../fx/ForceViz.js';
import ControlSurfaceAnimator from '../aircraft/ControlSurfaceAnimator.js';
import LandingGearAnimator    from '../aircraft/LandingGearAnimator.js';
import GroundContact          from '../aircraft/GroundContact.js';
import WeaponSystem           from '../weapons/WeaponSystem.js';
import MaverickSeeker         from '../weapons/MaverickSeeker.js';
import EnemyMissileSystem     from '../weapons/EnemyMissileSystem.js';
import ParticleSystem         from '../fx/ParticleSystem.js';
import ExplosionSystem        from '../fx/ExplosionSystem.js';
import SmokeTrailSystem       from '../fx/SmokeTrailSystem.js';
import FlareSystem            from '../fx/FlareSystem.js';
import AircraftShadow         from '../fx/AircraftShadow.js';
import CloudLayer             from '../fx/CloudLayer.js';
import CockpitOverlayPass      from '../fx/CockpitOverlayPass.js';
import FXOverlayPass, { FX_OVERLAY_LAYER } from '../fx/FXOverlayPass.js';
import FilmLookPass           from '../fx/FilmLookPass.js';
import filmPreset             from '../fx/presets/portra160Push.js';
import Terrain                from '../world/Terrain.js';
import TerrainCollider        from '../world/TerrainCollider.js';
import VegetationSystem       from '../world/VegetationSystem.js';
import Airfield               from '../world/Airfield.js';
import StarField              from '../world/StarField.js';
import RoadNetwork            from '../world/RoadNetwork.js';
import WindManager            from '../world/WindManager.js';
import AudioManager           from '../audio/AudioManager.js';
import MusicPlayer            from '../audio/MusicPlayer.js';
import { audioUrl } from '../audio/soundManifest.js';
import CockpitHUD             from '../cockpit/CockpitHUD.js';
import CockpitInstruments     from '../cockpit/CockpitInstruments.js';
import CockpitControls        from '../cockpit/CockpitControls.js';
import PilotHead              from '../cockpit/PilotHead.js';
import RWRDisplay             from '../cockpit/RWRDisplay.js';
import UIManager             from '../ui/UIManager.js';
import EnemyManager           from '../enemy/EnemyManager.js';
import { AIRCRAFT, ENV, CAMERA, RENDERING, SCENARIOS, START_SCENARIO, COMBAT_SPAWNS, LIGHTING, DEFAULT_LIGHTING, GAU8, FLARES, AUDIO, AUTOPILOT, TERRAIN, BOUNDS, CLOUDS, ROADS, STARS, MISSION, FILM_LOOK, INTRO } from './SimConfig.js';
import { load as loadSettings, save as saveSetting } from './UserSettings.js';
import { DEG2RAD, smoothstep, clamp } from '../utils.js';

const _fpvWorld = new THREE.Vector3();

// Render layer the transparent cockpit (canopy glass + HUD) is parked on while
// the cockpit camera is active, so the main RenderPass skips it and the
// CockpitOverlayPass can redraw it over the cloud deck. Must not collide with the
// Maverick seeker's HOT_LAYER (2).
const COCKPIT_OVERLAY_LAYER = 1;

// Render layer tagged (in addition to layer 0, so the beauty pass is unaffected)
// on the aircraft's opaque meshes. CockpitOverlayPass renders it depth-only to
// restore the occlusion the overlay's depth clear throws away — without it the
// canopy glass draws over the cockpit interior. Must not collide with
// COCKPIT_OVERLAY_LAYER (1) or the Maverick seeker's HOT_LAYER (2).
const COCKPIT_OCCLUDER_LAYER = 3;

// FX_OVERLAY_LAYER (4) is imported from FXOverlayPass — the layer the transparent
// FX (particle sprites, smoke-trail ribbons) are parked on while a cloud deck is
// up, so they redraw over it.

// NVG flip animation: how far above screen-centre (in UV) the optic disc parks
// while the goggles are stowed — far enough up to hide the whole lens housing.
const NVG_HIDE_Y = 1.35;

export default class App {
  constructor() {
    // ── User settings ─────────────────────────────────────────────────────────
    // Live copy of the defaults with the player's saved settings layered over
    // them. The settings overlay mutates these via applySetting(), which also
    // writes each change back to localStorage; they're re-applied whenever
    // per-scenario systems (Controller / GameHUD / CommandFeedback) are rebuilt.
    // Loaded first so the renderer, shadow map, and composer below can be built
    // straight into the saved quality settings rather than snapping to them.
    this.settings = loadSettings();
    this._fxFromLighting = { bloom: false, contrast: false, grain: false };

    // ── Renderer ──────────────────────────────────────────────────────────────
    // antialias:false — AA is handled by an MSAA render target on the composer below.
    // The renderer's own MSAA only applies to the default framebuffer, which EffectComposer bypasses.
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      logarithmicDepthBuffer: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio * this.settings.renderScale);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.5;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    document.getElementById('app').appendChild(this.renderer.domElement);
    this.renderer.info.autoReset = false;

    this.globalAnisotropy = Math.min(RENDERING.anisotropyCap, this.renderer.capabilities.getMaxAnisotropy());

    // ── Timer ───────────────────────────────────────────────────────────────
    // Timescale 0 == "stopped": update() still runs every frame (setAnimationLoop
    // keeps previous/current times fresh) but getDelta() returns 0, freezing sim
    // time without producing a jump when flipped back to 1 on launch/resume.
    this.timer = new THREE.Timer();
    this.timer.setTimescale(0); // don't auto-start

    // ── Sim state ────────────────────────────────────────────────────────────
    this.paused = true;
    this.debug = false;
    this._preview = false; // true while the live start-screen preview is running

    // Out-of-bounds grace countdown + forced-return guard (see _updateOutOfBounds)
    this._oobTimer = BOUNDS.timeout;
    this._oobResetting = false;

    // ── Flight sim instances (set per scenario) ─────────────────────────────
    this.aircraft = null;
    this.controller = null;
    this.cameraManager = null;
    // Transparent cockpit meshes (canopy glass + HUD) redrawn over the cloud deck
    // by the CockpitOverlayPass; rebuilt per scenario in _buildCockpitOverlaySet().
    this._cockpitOverlayMeshes = [];
    this.surfaceAnimator = null;
    this.gearAnimator = null;
    this.hud = null;
    this.gameHUD = null;
    this.commandFeedback = null;
    this.clickPrompt = null;
    this.outOfBoundsWarning = null;
    this.destroyedOverlay = null;
    this.missionCompleteOverlay = null;
    this._aircraftDestroyed = false; // edge-tracks aircraft.destroyed for the one-shot reaction
    this._aircraftTerrainImpact = false; // edge-tracks aircraft.terrainImpact (wreck hitting the ground)
    this._missionComplete = false;   // latched once all enemies are destroyed (combat only)
    this._missionCleared = false;    // latched on completion; survives resume so the pause menu can offer "End Mission"
    this._missionTracking = false;   // true while a combat scenario with live enemies can still be completed
    this._missionCompleteTimer = -1; // s left on the post-last-kill delay; <0 = not counting
    this.missionStats = null;        // per-combat-mission scoring/stats accumulator (null for non-combat)
    this.trainingMission = null;     // guided flight-school run (null outside training scenarios)
    this.intro = null;               // first-run intro overlay/timeline while it plays (see _startIntro)
    this.introMusic = null;          // the intro's play-once song; built only on a first run
    this._firstRunMission = false;   // this FT-101 run began with the intro (survives a restart)
    this._firstRunExitTimer = -1;    // s left on the hold before auto-exiting a first-run completion; <0 = idle
    this.jtacRadio = null;           // procedural ground-controller radio (combat only)
    this.cockpitHUD = null;
    this.cockpitInstruments = null;
    this.cockpitControls = null;
    this.pilotHead = null;
    this.rwrDisplay = null;
    this._instrumentMaterials = []; // cockpit emissive mats driven by engine power
    this.forceViz = null;
    this.groundContact = null;
    this.weaponSystem = null;
    this.maverickSeeker = null;
    this.enemyMissileSystem = null;
    this.particleSystem = null;
    this.explosionSystem = null;
    this.smokeTrailSystem = null;
    this.flareSystem = null;
    this.aircraftShadow = null;
    this.terrain = null;
    this.terrainCollider = null;
    this.roadNetwork = null;
    this.vegetation = null;
    this.airfield = null;
    this.starField = null;
    this.audioManager = null;
    this.enemyManager = null;
    this.windManager = new WindManager();

    // ── Scene ─────────────────────────────────────────────────────────────────
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x000000, 0, 1); // placeholder — set by applyLighting
    this._activeLighting = DEFAULT_LIGHTING;
    // Player-chosen cloud coverage from the start screen; null = use whatever the
    // active lighting condition's `clouds` block specifies.
    this._cloudCoverage = null;
    this.nvgOn = false; // night-vision goggles (Night condition only)
    // Flip-down animation: the optic disc swings in from the top edge and rattles
    // into place. The pass is kept enabled through the flip-up so it can animate
    // away before switching off. `_nvgAnimT` clocks the current swing.
    this._nvgAnimating = false;
    this._nvgAnimT = 0;

    // ── Sun light (configured per lighting scenario) ─────────────────────────
    this.sunLight = new THREE.DirectionalLight(0xffffff, 0);
    const sh = RENDERING.shadow;
    this.sunLight.castShadow = true;
    // Resolution comes from the user setting (whose default mirrors sh.mapSize),
    // so a saved choice is in place before the first shadow map is allocated.
    this.sunLight.shadow.mapSize.setScalar(this.settings.shadowResolution);
    this.sunLight.shadow.camera.near = sh.near;
    this.sunLight.shadow.camera.far = sh.far;
    this.sunLight.shadow.camera.left = -sh.frustumSize;
    this.sunLight.shadow.camera.right = sh.frustumSize;
    this.sunLight.shadow.camera.top = sh.frustumSize;
    this.sunLight.shadow.camera.bottom = -sh.frustumSize;
    this.sunLight.shadow.bias = sh.bias;
    this.sunLight.shadow.normalBias = sh.normalBias;
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);
    this._sunDirection = new THREE.Vector3(-0.5, -0.7, -0.4).normalize();

    this.ambientLight = new THREE.AmbientLight(0xffffff, 0);
    this.scene.add(this.ambientLight);

    this._shadowCockpitBlend = 0; // 0 = aircraft-centered, 1 = cockpit-focused

    // ── Camera ────────────────────────────────────────────────────────────────
    this.camera = new THREE.PerspectiveCamera(
      CAMERA.chase.fov,
      window.innerWidth / window.innerHeight,
      RENDERING.nearPlane,
      RENDERING.farPlane
    );
    this.camera.position.set(20, 5, 20);
    this.camera.lookAt(0, 0, 0);

    // ── Post-processing ───────────────────────────────────────────────────────
    const { innerWidth: w, innerHeight: h } = window;

    // 4× MSAA render target — AA happens pre-tone-mapping so it composites cleanly
    // with HDR bloom. HalfFloatType preserves HDR range through the pipeline.
    const msaaTarget = new THREE.WebGLRenderTarget(w, h, {
      type: THREE.HalfFloatType,
      samples: 4,
    });
    this.composer = new EffectComposer(this.renderer, msaaTarget);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // Scene depth for the volumetric cloud pass. Attaching a DepthTexture to both
    // composer targets makes the RenderPass (which renders into readBuffer with
    // needsSwap=false) resolve scene depth — through the 4× MSAA target — into a
    // texture the CloudLayer samples. Three re-syncs the depth texture's size to
    // the target on each setup, so it tracks composer resizes / AA changes for free.
    this.composer.renderTarget1.depthTexture = new THREE.DepthTexture(w, h);
    this.composer.renderTarget2.depthTexture = new THREE.DepthTexture(w, h);

    // Volumetric cloud deck — raymarched at reduced resolution and composited
    // straight after the scene (before bloom, so sunlit tops can bloom). Persists
    // across scenarios; self-disables for lighting conditions with no `clouds`
    // block and is reconfigured per condition in applyLighting().
    this.cloudLayer = new CloudLayer({ camera: this.camera });
    this.composer.addPass(this.cloudLayer);
    // Seed the pass from the default cloud quality preset. CloudLayer's
    // constructor already read the CLOUDS defaults, so this only does work when
    // the default setting isn't the preset those defaults mirror ('medium').
    this.setCloudQuality(this.settings.cloudQuality);

    // FX overlay — redraws the transparent FX (particle sprites, smoke-trail
    // ribbons) over the freshly-composited deck. Neither writes depth, so the
    // cloud pass paints over them; this pass relocates them past it while keeping
    // the scene depth test (see FXOverlayPass for why the depth is still valid
    // here). It MUST sit directly after the cloud pass, and before the cockpit
    // overlay so the canopy still covers the FX. _syncFXOverlay() enables it — and
    // parks the meshes on the layer — per frame.
    this.fxOverlayPass = new FXOverlayPass({ scene: this.scene, camera: this.camera });
    this.composer.addPass(this.fxOverlayPass);

    // Cockpit overlay — redraws the transparent cockpit (canopy glass + HUD) over
    // the freshly-composited clouds so the deck sits behind the glass instead of
    // painting over it. Inserted before bloom so the redrawn cockpit still flows
    // through bloom/grain/NVG unchanged (the HUD keeps its native glow). App parks
    // the cockpit's glass/HUD meshes on COCKPIT_OVERLAY_LAYER and enables this pass
    // only while the cockpit camera is active (see the render loop).
    this.cockpitOverlayPass = new CockpitOverlayPass({
      scene: this.scene,
      camera: this.camera,
      layer: COCKPIT_OVERLAY_LAYER,
      occluderLayer: COCKPIT_OCCLUDER_LAYER,
    });
    this.composer.addPass(this.cockpitOverlayPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      0, 0, 0, // set by applyLighting
    );
    this.composer.addPass(this.bloomPass);

    this.contrastPass = new ShaderPass({
      uniforms: {
        tDiffuse:    { value: null },
        uContrast:   { value: 1.0 },
        uBrightness: { value: 0.0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uContrast;
        uniform float uBrightness;
        varying vec2 vUv;
        void main() {
          vec4 c = texture2D(tDiffuse, vUv);
          // Contrast via pow — preserves black (0^x = 0) and maps 1→1
          c.rgb = pow(max(c.rgb, 0.0), vec3(uContrast)) + uBrightness;
          gl_FragColor = vec4(c.rgb, c.a);
        }
      `,
    });
    this.composer.addPass(this.contrastPass);

    this.grainPass = new ShaderPass({
      uniforms: {
        tDiffuse:  { value: null },
        uTime:     { value: 0 },
        uStrength: { value: 0 }, // set by applyLighting
      },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: filmGrainFrag,
    });
    this.composer.addPass(this.grainPass);

    this.composer.addPass(new OutputPass());

    // Film look — a Lightroom Classic preset (Kodak Portra 160 +1, via VSCO)
    // baked to a 3D LUT and applied as a final grade. It goes *after* OutputPass
    // because a Camera Raw preset is a grade on a finished, display-referred
    // image: its tone curves are drawn on a 0-255 axis and its black and white
    // points are meaningless against unbounded HDR. Unlike bloom/contrast/grain
    // it is not per-condition — it's one look for the whole sim, with no player
    // control: the strength is a build constant, so _applyPostFX gates it on that
    // alone (and on the post-processing toggle).
    this.filmLookPass = new FilmLookPass({
      // FILM_LOOK.adjust layers taste on top of the generated preset — see
      // SimConfig, and don't hand-edit fx/presets/, which is build output.
      preset: { ...filmPreset, ...FILM_LOOK.adjust },
      size: FILM_LOOK.lutSize,
      strength: this.settings.filmLook,
    });
    this.composer.addPass(this.filmLookPass);

    // Night-vision goggles (AN/AVS-9) — final-image pass, disabled until toggled
    // with N in the Night lighting condition. Runs after OutputPass so the green
    // phosphor tint and feathered black lens edge act on the tone-mapped image.
    this.nvgPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        uTime:    { value: 0 },
        uAspect:  { value: w / h },
        uZoom:    { value: 1 },
        // Optic-centre offset; +y keeps the disc parked above the top edge until
        // it flips down. NVG_HIDE_Y is far enough up to hide the whole housing.
        uDiscOffset: { value: new THREE.Vector2(0, NVG_HIDE_Y) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
      `,
      fragmentShader: nvgFrag,
    });
    this.nvgPass.enabled = false;
    this.composer.addPass(this.nvgPass);

    // Reconcile the composer's MSAA against the saved render-scale / AA settings
    // (the target above is built at 4×, the default). Bloom/contrast/grain need
    // no equivalent — applyLighting runs _applyPostFX() once the assets land.
    this._applyAA();

    // ── UI ────────────────────────────────────────────────────────────────────
    this.ui = new UIManager();
    this.ui.onEnter(() => this._onEnter());
    this.ui.onLaunch((cfg) => this.launch(cfg));
    this.ui.onResume(() => this.resume());
    this.ui.onRestart(() => this.restartScenario());
    this.ui.onEndMission(() => this.endMission());
    this.ui.onExitToStart(() => this.exitToStart());
    // Weather can also be changed from the pause menu mid-flight, so every
    // change is folded back into the launch config — a restart rebuilds from
    // that, and shouldn't revert the condition the player just picked.
    this.ui.onLightingChange((key) => { this.applyLighting(key); this._rememberWeather(); });
    this.ui.onArmamentChange((arm) => this.updatePreviewArmament(arm));
    this.ui.onCloudsChange((coverage) => { this.setCloudCoverage(coverage); this._rememberWeather(); });
    this.ui.onSettingChange((key, value) => this.applySetting(key, value));

    // ── Start-screen music ──────────────────────────────────────────────────
    // App-level looping track, independent of the per-scenario AudioManager.
    // Played on entry to the start screen, faded out + stopped on scenario entry.
    this.music = new MusicPlayer(
      import.meta.env.BASE_URL + encodeURI(AUDIO.music.file),
      {
        volume: AUDIO.music.volume,
        masterVolume: this.settings.masterVolume,
        musicVolume: this.settings.musicVolume,
      },
    );

    // ── Asset loading ─────────────────────────────────────────────────────────
    this.assets = new AssetManager(this.globalAnisotropy);
    this.assets.onProgress = (loaded, total) => this.ui.setProgress(loaded, total);

    Promise.all([this.assets.load(), CockpitHUD.loadFont()]).then(() => {
      // Star field — real Bright Star Catalogue positions drawn over the night
      // condition's starless plate. Shared across scenarios like the cloud deck,
      // and built before applyLighting so the first condition configures it.
      this.starField = new StarField({
        camera: this.camera,
        data: this.assets.getBinary(STARS.data),
      });
      this.starField.setPixelRatio(this.renderer.getPixelRatio());
      this.scene.add(this.starField.object);

      this.applyLighting(this._activeLighting);

      // Terrain is shared across scenarios. Hand it the cloud deck's shared
      // shadow uniforms so it casts soft cloud shadows on the ground (gated by
      // the global CLOUDS.shadow toggle so the shader cost is skipped when off).
      this.terrain = new Terrain({
        anisotropy: this.globalAnisotropy,
        cloudShadowUniforms: (CLOUDS.enabled && CLOUDS.shadow.enabled)
          ? this.cloudLayer?.shadowUniforms
          : null,
      });
      this.terrainCollider = new TerrainCollider(this.terrain.cache);
      this.scene.add(this.terrain.group);

      // Road graph — shared across scenarios like the terrain it sits on, and
      // read synchronously by EnemyManager when a scenario's `useRoads` spawns
      // are placed. Constructing it from a missing data file yields an empty
      // (not-ready) network, which those spawns handle by falling back.
      this.roadNetwork = new RoadNetwork(this.assets.getJSON(ROADS.data));
      // applyLighting ran above, before the terrain existed — seed its grading
      // from the active condition now. Later condition changes go through
      // applyLighting, which sets it directly.
      this.terrain.setGrading(LIGHTING[this._activeLighting]?.terrain);

      // Vegetation — loads its own splat + textures asynchronously; stays
      // disabled until those resolve. Per-asset loads are non-fatal.
      this.vegetation = new VegetationSystem({
        scene: this.scene,
        terrainCollider: this.terrainCollider,
        anisotropy: this.globalAnisotropy,
        basePath: import.meta.env.BASE_URL,
      });
      const lcfg = LIGHTING[this._activeLighting];
      if (lcfg) {
        const hdr = this.assets.getHDR(lcfg.hdr);
        this.vegetation.setLighting(hdr?.userData.skyTint, lcfg.effectBrightness);
      }

      // Buildings
      const buildings = this.assets.getModel('buildings');
      if (buildings) {
        buildings.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        this.scene.add(buildings);
      }

      // Airfield — snaps to terrain surface once the tile heightmap streams in
      const airfieldModel = this.assets.getModel('airfield');
      if (airfieldModel) {
        this.airfield = new Airfield({
          model: airfieldModel,
          scene: this.scene,
          terrainCollider: this.terrainCollider,
          asphaltTexture: this.assets.getTexture('asphalt'),
        });
      }

      // Assets are ready — reveal the "Enter" button on the loading screen.
      this.ui.assetsReady();
    });

    // ── Events ────────────────────────────────────────────────────────────────
    window.addEventListener('resize', () => this.onResize());
    window.addEventListener('keydown', (e) => {
      // Tilde/backquote toggles the debug overlay.
      if (e.code === 'Backquote' && !e.repeat) {
        this.debug = !this.debug;
        this.forceViz?.setVisible(this.debug);
        // this.terrain?.setDebug(this.debug);
        this.hud?.setVisible(this.debug);
      }
      if (e.code === 'Escape' && !e.repeat) this._onEscape();
      // R restarts the scenario from the "AIRCRAFT DESTROYED" prompt.
      if (e.code === 'KeyR' && !e.repeat
        && this.aircraft?.destroyed && this.ui.state === 'playing') {
        this.restartScenario();
      }
      // R dismisses the "MISSION COMPLETE" prompt and returns to free flight.
      if (e.code === 'KeyR' && !e.repeat && this._firstRunExitTimer < 0
        && this._missionComplete && this.ui.state === 'playing') {
        this._resumeFromMissionComplete();
      }
      // On the score sheet, R / Enter leaves it (mirrors the button): back to the
      // start screen when reviewing a saved run, else exit the scenario.
      if ((e.code === 'KeyR' || e.code === 'Enter') && !e.repeat
        && this.ui.state === 'ended') {
        this.ui.returnFromEnded();
      }
    });

    this.renderer.setAnimationLoop(() => this.animate());
  }

  // ── Scenario lifecycle ──────────────────────────────────────────────────────

  startScenario(scenario, opts = {}) {
    const { armament = null, preview = false } = opts;
    this._teardown();
    // Set after _teardown() — teardown resets this._preview to false.
    this._preview = preview;

    const a10 = this.assets.getModel('a10');
    if (!a10) return;

    // AIM-9 / AGM-65 loads come from the start-screen armament selection; the
    // preview (no selection yet) flies with empty pylons.
    const aim9Count = armament ? armament.aim9 : 0;
    const agm65Count = armament ? armament.agm65 : 0;

    this.aircraft = new Aircraft({
      model: a10,
      params: AIRCRAFT,
      startEngineOn: scenario.startEngineOn,
    });
    // Enable shadows on all A10 meshes (except glass)
    const noShadowMats = new Set(['M_A10_Glass', 'Cockpit_Glass']);
    a10.traverse((child) => {
      if (!child.isMesh) return;
      const matName = child.material?.name;
      if (noShadowMats.has(matName)) {
        child.castShadow = false;
        child.receiveShadow = false;
      } else {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    this.scene.add(this.aircraft.group);

    // Cache the cockpit's emissive materials so the per-frame brightness update
    // (engine-state gated) can drive them.
    this._collectInstrumentMaterials();

    this.controller = new Controller({ canvas: this.renderer.domElement, aircraft: this.aircraft, interactive: !preview });
    this.controller.lookYaw = (scenario.heading || 0) * DEG2RAD;
    this.controller.sensitivity = AUTOPILOT.sensitivity * this.settings.mouseSensitivity;
    this._applyControlOptions();

    const chaseCamera = new ChaseCamera({ aircraft: this.aircraft, controller: this.controller, terrainCollider: this.terrainCollider });
    const cockpitCamera = new CockpitCamera({ aircraft: this.aircraft, controller: this.controller });
    const enemyCamera = new EnemyCamera({ terrainCollider: this.terrainCollider });
    const startCamera = new StartCamera({ canvas: this.renderer.domElement, aircraft: this.aircraft });
    // Fixed camera mounts baked into the GLB — none in the model is simply no
    // action-camera mode (the C key becomes a no-op).
    const actionCamera = new ActionCamera({ model: a10 });
    this.cameraManager = new CameraManager({
      camera: this.camera,
      chaseCamera, cockpitCamera, enemyCamera, startCamera, actionCamera,
      controller: this.controller,
      // Live scenarios start in the cockpit; only the start-screen preview uses
      // the orbit camera. (A same-spawn launch glides in from the orbit pose via
      // introPose below; otherwise the cockpit is snapped to behind a fade.)
      initialMode: preview ? CameraMode.START : CameraMode.COCKPIT,
    });
    this.controller.cameraManager = this.cameraManager;
    // Camera cuts jump the view, so drop the clouds' temporal history to avoid a
    // few frames of reprojection ghosting after the switch.
    this.controller.onToggleCamera = () => { this.cameraManager.toggle(); this.cloudLayer?.resetHistory(); };
    this.controller.onCycleEnemyCamera = () => { this.cameraManager.cycleEnemy(this.enemyManager?.entities || []); this.cloudLayer?.resetHistory(); };
    this.controller.onCycleActionCamera = () => { this.cameraManager.cycleAction(); this.cloudLayer?.resetHistory(); };
    this.controller.onTogglePause = () => this.togglePause();
    this.controller.onToggleNVG = () => this.toggleNVG();

    // Spawn at scenario position
    this.aircraft.position.set(
      scenario.x,
      scenario.altitude,
      scenario.z,
    );
    const hdg = (scenario.heading || 0) * DEG2RAD;
    this.aircraft.velocity.set(
      -Math.sin(hdg) * scenario.velocity,
      0,
      -Math.cos(hdg) * scenario.velocity,
    );
    this.aircraft.quaternion.setFromEuler(new THREE.Euler(
      (Math.random() - 0.5) * scenario.randomAngle,
      hdg,
      (Math.random() - 0.5) * scenario.randomAngle,
      'YXZ',
    ));

    // Frame the start-screen orbit camera behind the now-positioned aircraft.
    if (preview) {
      startCamera.yaw = startCamera.targetYaw = hdg + CAMERA.start.yawOffset;
    }

    // Landing gear: set both target and current so there's no animation on spawn
    if (scenario.gearUp) {
      this.aircraft.gearTarget = 0;
      this.aircraft.gear = 0;
    }

    // Set throttle for level flight when spawning with velocity
    if (scenario.velocity > 0) {
      this.controller.throttle = this._levelFlightThrottle(scenario.velocity);
    }

    this.particleSystem = new ParticleSystem({
      scene: this.scene,
      atlas: this.assets.getTexture('particleAtlas'),
    });
    // Let the A-10 stream engine fire/smoke from its own update once damaged.
    this.aircraft.particleSystem = this.particleSystem;
    this.explosionSystem = new ExplosionSystem({
      scene: this.scene,
      assets: this.assets,
    });
    this.smokeTrailSystem = new SmokeTrailSystem({
      scene: this.scene,
      texture: this.assets.getTexture('smokeTrail'),
    });
    this.weaponSystem = new WeaponSystem({
      model: a10,
      scene: this.scene,
      aim9Model: this.assets.getModel('aim9'),
      aim9Count,
      agm65Model: this.assets.getModel('agm65'),
      agm65Count,
      gau8: armament ? armament.gau8 : 'full',
      unlimitedAmmo: armament ? !!armament.unlimited : false,
      particleSystem: this.particleSystem,
      smokeTrailSystem: this.smokeTrailSystem,
    });
    this.controller.weaponSystem = this.weaponSystem;
    this.cameraManager.cockpitCamera.weaponSystem = this.weaponSystem;
    this.flareSystem = new FlareSystem({
      scene: this.scene,
      particleSystem: this.particleSystem,
      smokeTrailSystem: this.smokeTrailSystem,
    });
    this.aircraft.countermeasures.flareSystem = this.flareSystem;
    // Flare loadout mirrors the GAU-8 ammo selection from the start screen.
    this.aircraft.countermeasures.count = armament && armament.flares === 'none' ? 0 : FLARES.capacity;
    this.aircraft.countermeasures.unlimited = armament ? !!armament.unlimitedFlares : false;
    this.enemyMissileSystem = new EnemyMissileSystem({
      scene: this.scene,
      missileModel: this.assets.getModel('missile'),
      particleSystem: this.particleSystem,
      smokeTrailSystem: this.smokeTrailSystem,
    });
    this.aircraftShadow = new AircraftShadow({
      scene: this.scene,
      texture: this.assets.getTexture('shadow'),
    });
    // Apply current lighting to particles + trails
    const lcfg = LIGHTING[this._activeLighting];
    if (lcfg) {
      const hdr = this.assets.getHDR(lcfg.hdr);
      this.particleSystem.setLighting(hdr?.userData.skyTint, lcfg.effectBrightness);
      this.explosionSystem.setLighting(hdr?.userData.skyTint, lcfg.effectBrightness);
      this.smokeTrailSystem.setLighting(hdr?.userData.skyTint, lcfg.effectBrightness);
    }

    this.surfaceAnimator = new ControlSurfaceAnimator(a10);
    this.gearAnimator = new LandingGearAnimator(a10);
    // Procedural head animation for the seated pilot. Built here rather than in
    // _buildGameplaySystems so the start-screen preview gets a living pilot too;
    // the combat references it reads for its threat picture are optional and
    // attached below (and again on promotion).
    this.pilotHead = new PilotHead({
      aircraft: this.aircraft,
      model: a10,
      cameraManager: this.cameraManager,
    });
    this.pilotHead.weaponSystem = this.weaponSystem;
    this.pilotHead.enemyMissileSystem = this.enemyMissileSystem;
    // Skip the deploy animation on spawn: jump the gear straight to its
    // scenario pose (aircraft.gear was already set from scenario.gearUp above).
    this.gearAnimator.snap(this.aircraft);
    this.groundContact = new GroundContact(a10);
    this.hud = new DebugHUD(this.aircraft, this.renderer, this.weaponSystem, this.particleSystem, this);
    this.hud.setVisible(this.debug);
    this.forceViz = new ForceViz(this.scene);
    this.audioManager = new AudioManager({
      camera: this.camera,
      aircraft: this.aircraft,
      cameraManager: this.cameraManager,
      weaponSystem: this.weaponSystem,
      enemyMissileSystem: this.enemyMissileSystem,
      assets: this.assets,
    });
    // Re-apply the user's master volume to the freshly rebuilt manager (the
    // scenario fade is driven separately on enter/exit).
    this.audioManager.setMasterVolume(this.settings.masterVolume);

    // ── Gameplay-only systems — skipped for the start-screen preview ──────────
    if (!preview) {
      this._buildGameplaySystems(scenario);
    } else {
      this.weaponSystem.setTargets([]);
    }

    if (preview) {
      // Live (not paused) so armament, animations, and future ambient effects
      // run — but the UI stays on the start screen and input is disabled.
      this.paused = false;
      this.timer.setTimescale(1);
      // The AudioManager is born silent; ease the start-screen ambience in so it
      // doesn't pop the instant the AudioContext resumes on first user gesture.
      this.audioManager.setMasterFade(1, AUDIO.enterFade);
    } else {
      this.resume();
    }
  }

  // Resolve the launch config's scenario index into a ready-to-spawn scenario.
  // Combat scenarios carry no player spawn pose of their own; the start screen's
  // "Start" dropdown picks one of COMBAT_SPAWNS, whose pose we merge on here
  // (leaving the scenario's enemy `spawns` and metadata intact). Non-combat
  // scenarios already embed their own fixed pose and pass through unchanged.
  _resolveScenario(cfg) {
    const scenario = SCENARIOS[cfg.scenarioIndex];
    if (scenario.category !== 'Combat') return scenario;
    const spawn = COMBAT_SPAWNS.find((s) => s.id === cfg.combatSpawn) || COMBAT_SPAWNS[0];
    const { id, label, jitter, ...pose } = spawn;
    if (jitter) {
      // Re-rolled on every launch/restart so the entry is never identical.
      const j = (m) => (Math.random() * 2 - 1) * m; // uniform in [-m, +m]
      pose.x += j(jitter.xz);
      pose.z += j(jitter.xz);
      pose.altitude += j(jitter.altitude);
      pose.heading += j(jitter.heading);
    }
    return { ...scenario, ...pose };
  }

  // Apply launch configuration from the start screen, then enter the scenario.
  //
  // Two transitions, chosen by whether the scenario's spawn matches the parked
  // start-screen aircraft:
  //  • Same spawn → the previewed A-10 is already exactly where the scenario
  //    needs it, so promote the live preview in place (no teardown/rebuild) and
  //    glide the orbit camera straight into the cockpit — no fade, no hitch.
  //  • Different spawn → the aircraft must jump elsewhere, so fade to black,
  //    rebuild the scenario while hidden, then fade back in (already in cockpit).
  // `opts` tunes the same-spawn glide (see promoteToScenario).
  launch(cfg, opts = {}) {
    // Remember the launch config so the pause menu can restart this scenario.
    this._launchCfg = cfg;
    // HUD visibility is locked in from the start screen (NO HUD reverses the
    // sense); the gameplay systems read this.settings.hud as they're built.
    this.settings.hud = !cfg.noHud;
    const scenario = this._resolveScenario(cfg);

    // Leaving the start screen for a scenario: fade the music out, then stop it.
    this.music.fadeOutAndStop(AUDIO.music.fadeOut);

    if (this.aircraft && this._preview && this._spawnMatchesPreview(scenario)) {
      // Lighting is already live on the preview; wind isn't visual — apply both
      // immediately, then promote in place and glide in.
      this._applyWeather(cfg);
      this.promoteToScenario(scenario, cfg, opts);
    } else {
      // Apply config + swap the scenario while the screen is black so the jump
      // (and any lighting change) is hidden. Duck the outgoing scenario's audio
      // under the black, then fade the rebuilt scenario's audio back in.
      if (this.audioManager) this.audioManager.setMasterFade(0, AUDIO.exitFade);
      this.ui.fadeToBlack().then(() => {
        this._applyWeather(cfg);
        this.startScenario(scenario, { armament: cfg.armament }); // builds a fresh AudioManager
        this.audioManager.setMasterFade(0);                       // start silent behind the black
        this.audioManager.setMasterFade(1, AUDIO.enterFade);      // fade in as we return
        return this.ui.fadeFromBlack();
      });
    }
  }

  // Apply a launch config's weather: lighting condition, the cloud coverage the
  // player dialled in over it (applyLighting drops the override on a condition
  // change, so it's re-asserted after), and the wind preset.
  _applyWeather(cfg) {
    this.applyLighting(cfg.lighting);
    if (cfg.cloudCoverage != null) this.setCloudCoverage(cfg.cloudCoverage);
    this.windManager.setPreset(cfg.wind);
  }

  // Fold the live weather state back into the launch config, so a restart from
  // the pause menu rebuilds with the condition the player is actually flying in
  // rather than the one they launched with. The coverage override rides along:
  // applyLighting drops it on a condition change (the new condition brings its
  // own tuned deck), and a null here leaves _applyWeather to do the same.
  _rememberWeather() {
    if (!this._launchCfg) return;
    this._launchCfg.lighting = this._activeLighting;
    this._launchCfg.cloudCoverage = this._cloudCoverage;
  }

  // True when a scenario spawns the A-10 in the exact same parked pose as the
  // start-screen preview — i.e. nothing visible needs to move, so the preview
  // can be promoted in place and the camera glided in (vs. fading + rebuilding).
  // Compares every field that affects the resting aircraft pose.
  _spawnMatchesPreview(scenario) {
    const keys = ['x', 'z', 'altitude', 'heading', 'randomAngle', 'velocity'];
    return keys.every((k) => (scenario[k] || 0) === (START_SCENARIO[k] || 0));
  }

  // Promote the live start-screen preview into a playable scenario WITHOUT a
  // teardown/rebuild. The preview already holds the aircraft and every heavy
  // system (weapons, particle/trail pools, audio) built and sitting in the
  // exact spawn pose, so re-creating them would only churn GPU buffers and hitch
  // the camera glide. We reconcile the few fields that differ from the cold,
  // input-disabled preview, attach the gameplay-only systems it skipped, and
  // hand control to the player.
  //
  // `glideDuration` is the camera's glide into the cockpit; `audioLive` says the
  // preview's audio is already up and must not be snapped silent (the intro,
  // whose ambient bed and engine start-up have been playing under its shots).
  promoteToScenario(scenario, cfg, { glideDuration = CAMERA.start.introDuration, audioLive = false } = {}) {
    this._preview = false;

    // The preview was silent (cold engine); the scenario may start the engine
    // running, which would otherwise pop its loops in at full volume. Snap audio
    // to silent now, then fade it in across the camera glide below.
    if (!audioLive) this.audioManager.setMasterFade(0);

    // Reconcile parked state with the scenario (preview is always cold & dark).
    if (scenario.startEngineOn && !this.aircraft.engineState.isRunning) {
      this.aircraft.setEngineRunning();
    }
    if (scenario.gearUp) { this.aircraft.gearTarget = 0; this.aircraft.gear = 0; }

    // Apply the final armament selection (the preview only tracks AIM-9 live).
    if (cfg.armament) {
      this.weaponSystem.ammo = cfg.armament.gau8 === 'none' ? 0 : GAU8.ammoCapacity;
      this.weaponSystem.unlimitedAmmo = !!cfg.armament.unlimited;
      this.weaponSystem.setAIM9Count(cfg.armament.aim9);
      this.weaponSystem.setAGM65Count(cfg.armament.agm65);
      this.aircraft.countermeasures.count = cfg.armament.flares === 'none' ? 0 : FLARES.capacity;
      this.aircraft.countermeasures.unlimited = !!cfg.armament.unlimitedFlares;
    }

    // Attach the gameplay-only systems the preview skipped, then take input.
    this._buildGameplaySystems(scenario);
    this.controller.enableInput();

    // Glide from the orbit beauty shot straight into the cockpit. The captured
    // pose is the camera's current (orbit) frame; introTo seeds the transition
    // from it and eases into the cockpit over a cinematic duration.
    this.cameraManager.introTo(
      CameraMode.COCKPIT,
      { position: this.camera.position.clone(), quaternion: this.camera.quaternion.clone(), fov: this.camera.fov },
      glideDuration,
    );

    // Ramp audio in alongside the cinematic glide into the cockpit.
    this.audioManager.setMasterFade(1, AUDIO.enterFade);

    this.resume();
  }

  // Build the gameplay-only systems (cockpit HUD/instruments/controls, enemies,
  // combat HUD). Shared by startScenario (full spawn) and promoteToScenario
  // (in-place promotion of the preview).
  _buildGameplaySystems(scenario) {
    const a10 = this.assets.getModel('a10');

    // Control scheme — the player's choice everywhere, Flight School included
    // (whose voiced scripts still describe Basic's look-to-steer controls).
    this._applyControlScheme({ snap: true });
    this.controller.onPadMenu = () => this._onPadMenu();
    this.controller.onDeviceStatus = (text) => this.commandFeedback?.flash(text);
    this.controller.onDeviceLost = (text) => {
      this.commandFeedback?.flash(text);
      // Losing the stick mid-flight is a crash waiting to happen — hold the
      // sim on the pause menu until the player has it back.
      if (this.ui.state === 'playing' && !this.paused && this._firstRunExitTimer < 0) {
        this.pause();
        this.ui.showPause(this._missionCleared);
      }
    };

    this.cockpitHUD = new CockpitHUD({
      aircraft: this.aircraft,
      model: a10,
      cameraManager: this.cameraManager,
      weaponSystem: this.weaponSystem,
      terrainCollider: this.terrainCollider,
      windManager: this.windManager,
    });
    this.cockpitInstruments = new CockpitInstruments({
      aircraft: this.aircraft,
      model: a10,
      cameraManager: this.cameraManager,
    });
    this.cockpitControls = new CockpitControls({
      aircraft: this.aircraft,
      model: a10,
      cameraManager: this.cameraManager,
    });

    this.enemyManager = new EnemyManager({
      scene: this.scene,
      assets: this.assets,
      terrainCollider: this.terrainCollider,
      particleSystem: this.particleSystem,
      explosionSystem: this.explosionSystem,
      audioManager: this.audioManager,
      flareSystem: this.flareSystem,
      missileSystem: this.enemyMissileSystem,
      roadNetwork: this.roadNetwork,
    });
    // Player pose is already set above, so player-relative spawns resolve here.
    this.enemyManager.spawnForScenario(scenario, this.aircraft);
    this.weaponSystem.setTargets(this.enemyManager.entities);
    this.audioManager.setEnemyManager(this.enemyManager);

    // AGM-65 Maverick seeker + TV display. Needs the enemy list (lock-on +
    // white-hot tagging); wires into the controller (aim input), the cockpit
    // camera (aim pose) and the cockpit HUD (aim reticle).
    this.maverickSeeker = new MaverickSeeker({
      scene: this.scene,
      aircraft: this.aircraft,
      model: a10,
      weaponSystem: this.weaponSystem,
      cameraManager: this.cameraManager,
      terrainCollider: this.terrainCollider,
      enemyManager: this.enemyManager,
    });
    this.controller.maverickSeeker = this.maverickSeeker;
    this.cameraManager.cockpitCamera.maverickSeeker = this.maverickSeeker;
    this.cockpitHUD.maverickSeeker = this.maverickSeeker;
    // Hand the pilot his threat picture: who's out there, and whether he's
    // heads-down on the Maverick TV.
    if (this.pilotHead) {
      this.pilotHead.enemyManager = this.enemyManager;
      this.pilotHead.maverickSeeker = this.maverickSeeker;
      this.pilotHead.weaponSystem = this.weaponSystem;
      this.pilotHead.enemyMissileSystem = this.enemyMissileSystem;
    }

    // RWR threat scope on the cockpit `_RWR` plane — reads the same enemy list
    // as the game HUD plus the live enemy missiles. Built before
    // _buildCockpitOverlaySet() below so its (opaque) screen material is tagged
    // as a cockpit occluder like the rest of the panel.
    this.rwrDisplay = new RWRDisplay({
      aircraft: this.aircraft,
      model: a10,
      cameraManager: this.cameraManager,
      enemyManager: this.enemyManager,
      enemyMissileSystem: this.enemyMissileSystem,
      assets: this.assets,
    });

    // Guided training run — built for any scenario carrying a `training` key.
    // Owns its own objective board + instructor captions, and feeds the game
    // HUD the waypoint its current objective wants the player to fly to.
    const trainingDef = getTrainingMission(scenario.training);
    this.trainingMission = trainingDef
      ? new TrainingMission({
          definition: trainingDef,
          app: this,
          aircraft: this.aircraft,
          controller: this.controller,
          terrainCollider: this.terrainCollider,
          enemyManager: this.enemyManager,
        })
      : null;
    if (this.trainingMission) {
      this.trainingMission.onComplete = () => this._onMissionComplete();
    }

    this.gameHUD = new GameHUD({
      aircraft: this.aircraft,
      controller: this.controller,
      cameraManager: this.cameraManager,
      enemyManager: this.enemyManager,
      camera: this.camera,
      trainingMission: this.trainingMission,
    });

    this.commandFeedback = new CommandFeedback({
      app: this,
      controller: this.controller,
      aircraft: this.aircraft,
      weaponSystem: this.weaponSystem,
    });

    // Apply the current HUD visibility setting to the freshly built HUDs.
    this.gameHUD.setVisible(this.settings.hud);
    this.commandFeedback.setVisible(this.settings.hud);

    // Scoring + the "end mission" flow are combat-only. The stats accumulator
    // collects events through a `.stats` reference on each reporting system.
    const isCombat = scenario.category === 'Combat';
    // Mission code drives persistence (score/grade/stats keyed by code); kept
    // for endMission() to write the result once the run finishes.
    this._scenarioCode = scenario.code || null;
    // Lock the settings NO HUD toggle in combat (it's an armed launch modifier
    // there); resync the control to this scenario's live HUD state either way.
    this.ui.setHudLocked(isCombat, this.settings.hud);
    // Lighting feeds the night-ops score bonus, so it's fixed in combat too —
    // lock the pause menu's lighting picker.
    this.ui.setLightingLocked(isCombat);
    this.missionStats = isCombat
      ? new MissionStats(this.aircraft, {
          night: this._activeLighting === 'night',
          noHud: !this.settings.hud,
          unlimitedAmmo: this.weaponSystem.unlimitedAmmo,
          unlimitedFlares: this.aircraft.countermeasures.unlimited,
        })
      : null;
    if (this.missionStats) {
      this.weaponSystem.stats = this.missionStats;
      this.enemyManager.stats = this.missionStats;
      this.enemyMissileSystem.stats = this.missionStats;
      this.aircraft.countermeasures.stats = this.missionStats;
    }

    // Mission radio — field tower plus, in combat, the ground controller. Built
    // after the stats accumulator because the controller reads the munition
    // counters off it to bracket gun runs and Maverick shots. A pure observer
    // otherwise: it polls the world rather than being reported to, so nothing
    // above has to know it exists.
    //
    // Skipped only for training, where the instructor owns the net — tower
    // clearances over a flight lesson about how to taxi would be talking across
    // the one voice the player actually has to listen to. `jtac` is what gates
    // the controller's half to combat; Free Flight gets the tower alone.
    this.jtacRadio = this.trainingMission
      ? null
      : new JTACRadio({
          aircraft: this.aircraft,
          enemyManager: this.enemyManager,
          enemyMissileSystem: this.enemyMissileSystem,
          stats: this.missionStats,
          jtac: isCombat,
          masterVolume: this.settings.masterVolume,
          jtacVolume: this.settings.jtacVolume,
        });

    // Cockpit meshes the CloudLayer must not paint over (redrawn by the overlay
    // pass); needs the cockpit HUD mesh, so build it after CockpitHUD above.
    this._buildCockpitOverlaySet();

    this.clickPrompt = new ClickPrompt();
    this.outOfBoundsWarning = new OutOfBoundsWarning();
    this.destroyedOverlay = new DestroyedOverlay({ canEndMission: isCombat });
    this.missionCompleteOverlay = new MissionCompleteOverlay({ canEndMission: isCombat });
    this._aircraftDestroyed = false;
    this._missionComplete = false;
    this._missionCleared = false;
    // Mission completion only applies to combat scenarios that actually spawned
    // enemies — otherwise an empty roster would trip "complete" on the first frame.
    this._missionTracking = isCombat && this.enemyManager.entities.length > 0;
    this._missionCompleteTimer = -1;
    this._oobTimer = BOUNDS.timeout;
    this._oobResetting = false;
  }

  // Loading screen's ENTER, behind its fade to black. A first run goes into the
  // intro instead of the start screen; returns true when it has (UIManager
  // then shows no screen and leaves the picture to the intro's overlay).
  _onEnter() {
    const scenarioIndex = SCENARIOS.findIndex((s) => s.training === INTRO.training);
    if (scenarioIndex < 0 || !shouldPlayIntro()) {
      this.enterStartPreview();
      return false;
    }
    this._startIntro(scenarioIndex);
    return true;
  }

  // First-run intro: a typed title card, a few slow shots of the parked A-10,
  // then a glide into the cockpit as FT-101 begins. FT-101 spawns exactly where
  // the start-screen preview parks, so the preview *is* the intro's set, and
  // launch() promotes it in place at the end — the same path "Fly Now" takes,
  // with a longer glide and the ambient bed already up. Nothing here touches the
  // first-run flag; finishing FT-101 or a deliberate Exit to Menu does that.
  _startIntro(scenarioIndex) {
    // The start screen's own selection (wind, NO HUD) with the mission and the
    // condition pinned; the condition's tuned cloud coverage, not the slider's.
    const cfg = this.ui.launchConfig({ scenarioIndex, lighting: INTRO.lighting, cloudCoverage: null });
    this._applyWeather(cfg);
    this.enterStartPreview({ music: false });
    // Silent behind the title card; the ambient bed comes up with the picture.
    this.audioManager?.setMasterFade(0);

    const introCamera = new IntroCamera({ aircraft: this.aircraft, shots: INTRO.shots });
    // Every cut jumps the view — drop the clouds' reprojection history with it.
    introCamera.onCut = () => this.cloudLayer?.resetHistory();
    // The engines are cranked on a cue in the shots, so the start-up sequence
    // plays under them — and FT-101's "start the engines" step, finding them
    // already turning, skips itself. Not a toggle: a skip followed by the cue
    // must never shut them down again.
    const startEngines = () => {
      if (this.aircraft?.engineState.isOff) this.aircraft.toggleEngine();
    };
    introCamera.addCue(INTRO.engineStart.shot, INTRO.engineStart.at, startEngines);
    introCamera.update(0);
    this.cameraManager.playIntro(introCamera);

    // Built here rather than up front, so a returning player never fetches it.
    this.introMusic?.dispose();
    this.introMusic = new MusicPlayer(audioUrl(INTRO.music.file), {
      volume: INTRO.music.volume,
      masterVolume: this.settings.masterVolume,
      musicVolume: this.settings.musicVolume,
      loop: false,
    });

    this.intro = new IntroSequence({
      parent: this.ui.root,
      config: INTRO,
      camera: introCamera,
      volume: this.settings.masterVolume,
      onTyped: () => this.introMusic?.play(),
      onReveal: () => {
        this.cloudLayer?.resetHistory();
        this.audioManager?.setMasterFade(1, INTRO.audioFadeIn);
      },
      onFinish: () => {
        // Its completion hands the player to the start screen (_onMissionComplete).
        this._firstRunMission = true;
        startEngines(); // skipped before the start-up cue
        this.launch(cfg, { glideDuration: INTRO.glideDuration, audioLive: true });
      },
    });
  }

  // Spawn the live "beauty shot" preview shown behind the start screen.
  // `music: false` is the first-run intro, which uses the preview as its set.
  enterStartPreview({ music = true } = {}) {
    // Start the looping start-screen music. Covers both entry paths into the
    // start screen: from the loading screen and on return from a scenario. If a
    // track is already sounding (the victory theme still playing behind the
    // mission-ended screen — same MusicPlayer), let it run on seamlessly rather
    // than snapping back to 0:00.
    if (music && !this.music.isPlaying) this.music.play();

    const sel = this.ui.selection;
    // Effective loadout, so a mission that pins one (training flies clean)
    // previews with the pylons it will actually launch with.
    const armament = sel ? this.ui.armament : null;
    this.windManager.setPreset(sel?.wind || 'none');
    this.startScenario(START_SCENARIO, { armament, preview: true });
  }

  // Update the live preview's armament in realtime as the panel changes.
  updatePreviewArmament(armament) {
    if (!this._preview || !this.weaponSystem) return;
    this.weaponSystem.setAIM9Count(armament.aim9);
    this.weaponSystem.setAGM65Count(armament.agm65);
  }

  // Restart the current scenario from the pause menu. Rebuilds it fresh from the
  // last launch config behind the same fade-to-black used for repositioning
  // launches; startScenario() resumes play (paused → playing) on its own.
  restartScenario() {
    if (this._exiting || !this._launchCfg) return;
    const cfg = this._launchCfg;
    const scenario = this._resolveScenario(cfg);
    this._exiting = true;
    if (this.audioManager) this.audioManager.setMasterFade(0, AUDIO.exitFade);
    // The JTAC is an HTMLAudioElement, outside the graph that fade drives — he
    // has to be taken off the air by hand or he talks over the fade to black.
    this.jtacRadio?.close();
    // Likewise the intro's song, if it's still running under FT-101.
    this.introMusic?.fadeOutAndStop(INTRO.music.fadeOut);
    this.ui.fadeToBlack().then(() => {
      this._applyWeather(cfg);
      this.startScenario(scenario, { armament: cfg.armament }); // builds a fresh AudioManager
      this.audioManager.setMasterFade(0);                       // start silent behind the black
      this.audioManager.setMasterFade(1, AUDIO.enterFade);      // fade in as we return
      return this.ui.fadeFromBlack();
    }).then(() => { this._exiting = false; });
  }

  // Tear down the running scenario and return to the start screen preview.
  // The cockpit → parked orbit beauty shot is always a jump, so hide the swap
  // behind the same fade-to-black used for repositioning launches.
  exitToStart() {
    if (this._exiting) return;
    this._exiting = true;
    // A deliberate exit lands the player on the start screen, which is all the
    // intro was standing in for: from here on they boot straight to it.
    markIntroSeen();
    this._firstRunMission = false;
    // The intro's song gives way to the menu theme rather than playing over it.
    this.introMusic?.fadeOutAndStop(INTRO.music.fadeOut);
    // Fade the scenario's audio out under the black. The rebuilt preview is
    // silent (cold engine), so there's nothing to fade back in.
    if (this.audioManager) this.audioManager.setMasterFade(0, AUDIO.exitFade);
    // The JTAC is an HTMLAudioElement, outside the graph that fade drives — he
    // has to be taken off the air by hand or he talks over the fade to black.
    this.jtacRadio?.close();
    this.ui.fadeToBlack().then(() => {
      // Drop the NVGs when leaving a Night scenario — the start preview has its
      // own lighting and the goggles shouldn't carry over into it.
      this.nvgOn = false;
      this._resetNVGFlip();
      this.enterStartPreview();
      this.ui.showStart();
      // Back on the start screen there's no active combat scenario, so the
      // settings NO HUD toggle and the pause-menu lighting picker are free again.
      this.ui.setHudLocked(false, this.settings.hud);
      this.ui.setLightingLocked(false);
      return this.ui.fadeFromBlack();
    }).then(() => { this._exiting = false; });
  }

  // Throttle (0–1) that balances drag for steady level flight at airspeed V
  // (m/s), rounded up to the nearest 5%. Shared by the spawn seeding above and
  // the out-of-bounds return-to-combat reset.
  _levelFlightThrottle(V) {
    const q = 0.5 * ENV.airDensity * V * V;
    // CL required for level flight (lift = weight)
    const weight = AIRCRAFT.mass * ENV.gravity;
    const CL = weight / (q * AIRCRAFT.wingArea);
    // Forward drag (body-aligned, clean config)
    const dc = AIRCRAFT.dragCoefficients.forward;
    const forwardDrag = q * dc.cd * dc.area;
    // Induced drag
    const AR = (AIRCRAFT.wingSpan * AIRCRAFT.wingSpan) / AIRCRAFT.wingArea;
    const CDi = (CL * CL) / (Math.PI * AR * 0.8);
    const inducedDrag = q * AIRCRAFT.wingArea * CDi;
    // Thrust lapse at this airspeed
    const lapse = 1 - AIRCRAFT.thrustLapse * (V / AIRCRAFT.thrustLapseSpeed);
    const t = (forwardDrag + inducedDrag) / (AIRCRAFT.maxThrust * lapse);
    return Math.max(0, Math.min(1, Math.ceil(t * 20) / 20));
  }

  // True when the aircraft is horizontally outside the play area (XZ only —
  // altitude never counts as out-of-bounds).
  _isOutOfBounds() {
    const p = this.aircraft.position;
    const [ox, oz] = TERRAIN.origin;
    return Math.abs(p.x - ox) > BOUNDS.halfX || Math.abs(p.z - oz) > BOUNDS.halfZ;
  }

  // Per-frame out-of-bounds bookkeeping: count down the grace timer while the
  // player is outside the map, surface the "RETURN TO COMBAT" warning, and trip
  // a forced return when it expires. Returning in-bounds re-arms the timer.
  _updateOutOfBounds(dt) {
    const warn = this.outOfBoundsWarning;
    if (!warn || !this.aircraft) return;

    // A destroyed A-10 is a wreck, not a stray player — kill the timer so the
    // out-of-bounds system never respawns it (the DESTROYED overlay owns restart).
    if (this.aircraft.destroyed) { this._oobTimer = BOUNDS.timeout; warn.update(false); return; }

    // Hold steady (no warning, frozen timer) while a forced return is mid-fade.
    if (this._oobResetting) { warn.update(false); return; }

    if (this._isOutOfBounds()) {
      this._oobTimer -= dt;
      if (this._oobTimer <= 0) {
        warn.update(false);
        this._forceReturnToBounds();
        return;
      }
      warn.update(true, this._oobTimer);
    } else {
      this._oobTimer = BOUNDS.timeout;
      warn.update(false);
    }
  }

  // Fade to black, snap the aircraft back inside the boundary in a stable,
  // level configuration, then fade back in. Mirrors the audio/fade choreography
  // of the reposition launches but keeps every system in place (no rebuild).
  _forceReturnToBounds() {
    if (this._oobResetting || this._exiting) return;
    this._oobResetting = true;
    if (this.audioManager) this.audioManager.setMasterFade(0, AUDIO.exitFade);
    // The JTAC is outside the Web Audio graph that fade drives, so he has to be
    // cut by hand. Silenced rather than closed: nothing is being rebuilt here,
    // and he picks the mission back up on the far side of the black.
    this.jtacRadio?.silence();
    this.ui.fadeToBlack().then(() => {
      this._repositionInBounds();
      this.audioManager.setMasterFade(0);                  // start silent behind the black
      this.audioManager.setMasterFade(1, AUDIO.enterFade); // fade in as we return
      return this.ui.fadeFromBlack();
    }).then(() => {
      this._oobTimer = BOUNDS.timeout;
      this._oobResetting = false;
    });
  }

  // Place the aircraft just inside the nearest boundary edge at its current
  // altitude, levelled out and facing the centre of the map, flying at its
  // current (clamped) airspeed with the throttle trimmed for level flight.
  _repositionInBounds() {
    const a = this.aircraft;
    const [ox, oz] = TERRAIN.origin;

    // 1. Clamp horizontal position to just inside the boundary; keep altitude,
    //    but lift it to a safe AGL floor (terrain at the boundary can sit at or
    //    above the player's current altitude, which would respawn them buried).
    const m = BOUNDS.insetMargin;
    a.position.x = Math.max(ox - BOUNDS.halfX + m, Math.min(ox + BOUNDS.halfX - m, a.position.x));
    a.position.z = Math.max(oz - BOUNDS.halfZ + m, Math.min(oz + BOUNDS.halfZ - m, a.position.z));
    const groundY = this.terrainCollider
      ? (this.terrainCollider.getHeightAt(a.position.x, a.position.z) ?? 0)
      : 0;
    a.position.y = Math.max(a.position.y, groundY + BOUNDS.minClearance);

    // 2. Heading that points the nose at the map centre. forwardVector for
    //    heading h is (-sin h, 0, -cos h), so h = atan2(px - ox, pz - oz)
    //    yields a forward vector aimed back toward the origin.
    const hdg = Math.atan2(a.position.x - ox, a.position.z - oz);

    // 3. Wings-level, zero rotation.
    a.quaternion.setFromEuler(new THREE.Euler(0, hdg, 0, 'YXZ'));
    a.angularVelocity.set(0, 0, 0);

    // 4. Horizontal velocity along the new heading at a flyable speed.
    const speed = Math.max(BOUNDS.resetSpeed.min, Math.min(BOUNDS.resetSpeed.max, a.velocity.length()));
    a.velocity.set(-Math.sin(hdg) * speed, 0, -Math.cos(hdg) * speed);

    // 5. Trim throttle for steady level flight at that speed, and re-aim the
    //    bank-to-turn autopilot at the new heading so it holds level instead of
    //    immediately yanking back toward the boundary.
    this.controller.throttle = this._levelFlightThrottle(speed);
    this.controller.lookYaw = hdg;
    this.controller.lookPitch = 0;
    this.controller.freelook = false;
    this.controller.recenter();

    // Keep the scene graph in step so the fade-in shows the corrected pose.
    a.syncSceneGraph();
  }

  // One-shot reaction to the A-10 being destroyed by a terrain crash (latched on
  // aircraft.destroyed): spawn a MIG-style explosion at the wreck, cut all A-10
  // audio (the blast still rings out), force the view out of the cockpit, and
  // reveal the "AIRCRAFT DESTROYED" overlay. Controls are already dead via the
  // Controller's destroyed gate. R restarts the scenario (see the keydown above).
  _onAircraftDestroyed() {
    this._aircraftDestroyed = true;
    // The wreck is watched from the level chase framing; in an airframe-locked
    // scheme the world look is stale, so point it down the final heading.
    if (this.controller?.bodyLook) this.controller.resetLookToNose();
    // A crash after clearing the mission overrides the victory state: drop the
    // "MISSION COMPLETE" prompt and victory theme so the destroyed flow owns the
    // end of the run (otherwise both R handlers would fire on the next keypress).
    this._missionCompleteTimer = -1;
    this._firstRunExitTimer = -1;
    if (this._missionComplete) {
      this._missionComplete = false;
      this._missionTracking = false;
      this.missionCompleteOverlay?.hide();
      this.music.fadeOutAndStop(AUDIO.music.fadeOut);
    }
    const p = this.aircraft.position;
    if (this.particleSystem) {
      this.particleSystem.spawnExplosion(p.x, p.y, p.z, { size: AIRCRAFT.crashDamage.explosionSize });
    }
    if (this.audioManager) {
      this.audioManager.playExplosion('Explosion_Crash', p.x, p.y, p.z);
      this.audioManager.silence();
    }
    this.jtacRadio?.close();
    this.cameraManager?.forceDestroyed();
    this.destroyedOverlay?.show();
  }

  // One-shot reaction to a wreck — already destroyed in the air — slamming into
  // the terrain (latched on aircraft.terrainImpact): a second explosion at the
  // impact point. The destroyed reaction already silenced A-10 audio, but the
  // blast itself queues into the explosion pool, which silence() doesn't touch.
  _onAircraftTerrainImpact() {
    this._aircraftTerrainImpact = true;
    const p = this.aircraft.position;
    if (this.particleSystem) {
      this.particleSystem.spawnExplosion(p.x, p.y, p.z, { size: AIRCRAFT.crashDamage.explosionSize });
    }
    if (this.audioManager) {
      this.audioManager.playExplosion('Explosion_Crash', p.x, p.y, p.z);
    }
  }

  // One-shot reaction to clearing a mission — every enemy destroyed in combat,
  // or the last objective cleared in a training run (latched on
  // _missionComplete): reveal the "MISSION COMPLETE" overlay (it fades in over
  // two seconds via CSS) and bring the victory theme up from 1:13. Gameplay keeps
  // running underneath — the player flies on until R dismisses the prompt.
  _onMissionComplete() {
    this._missionComplete = true;
    // Through a flight-school lesson — the first-run intro has done its job.
    if (this.trainingMission) markIntroSeen();
    // Latched separately from the prompt flag: stays set after the player
    // resumes free flight so the pause menu can still offer "End Mission".
    // Only combat has a score sheet to end into, so training never arms it.
    this._missionCleared = !!this.missionStats;
    this.missionStats?.setMissionComplete();
    // A first-time player who just finished the intro's FT-101 isn't offered
    // free flight: no prompt, and after a beat they're taken to the start
    // screen (see the tick in animate). The victory theme carries straight on
    // into it, since enterStartPreview leaves a playing track alone.
    const autoExit = this._firstRunMission && !!this.trainingMission;
    this.missionCompleteOverlay?.setPromptVisible(!autoExit);
    if (autoExit) this._firstRunExitTimer = INTRO.completeExitDelay;
    this.missionCompleteOverlay?.show();
    this.music.playFrom(AUDIO.music.victoryStart, AUDIO.music.victoryFadeIn);
    // Duck all world/A-10 audio (engine, gun, RWR, explosions) so only the
    // victory theme is left swelling. Restored on resume below.
    this.audioManager?.setMasterFade(0, AUDIO.music.victoryWorldFadeOut);
    // The victory theme owns the mix now, and there is nothing left to control.
    // Not routed through setMasterFade: the JTAC is an HTMLAudioElement, outside
    // the Web Audio graph that fade drives.
    this.jtacRadio?.close();
  }

  // Dismiss the "MISSION COMPLETE" prompt (R) and return to free flight over the
  // cleared battlefield: hide the overlay and fade the victory theme out over the
  // same duration used when entering a scenario. Tracking stays off so it can't
  // re-trigger.
  _resumeFromMissionComplete() {
    this._missionComplete = false;
    this._missionTracking = false;
    this._missionCompleteTimer = -1;
    this.missionCompleteOverlay?.hide();
    this.music.fadeOutAndStop(AUDIO.music.fadeOut);
    // Bring world/A-10 audio back as the theme fades out (mirrors entering a scenario).
    this.audioManager?.setMasterFade(1, AUDIO.enterFade);
  }

  _teardown() {
    if (this.controller)      { this.controller.dispose(); this.controller = null; }
    if (this.aircraftShadow)  { this.aircraftShadow.dispose(); this.aircraftShadow = null; }
    if (this.flareSystem)     { this.flareSystem.dispose(); this.flareSystem = null; }
    if (this.particleSystem)  { this.particleSystem.dispose(); this.particleSystem = null; }
    if (this.explosionSystem) { this.explosionSystem.dispose(); this.explosionSystem = null; }
    if (this.smokeTrailSystem){ this.smokeTrailSystem.dispose(); this.smokeTrailSystem = null; }
    if (this.maverickSeeker)  { this.maverickSeeker.dispose(); this.maverickSeeker = null; }
    if (this.weaponSystem)    { this.weaponSystem.dispose(); this.weaponSystem = null; }
    if (this.enemyMissileSystem) { this.enemyMissileSystem.dispose(); this.enemyMissileSystem = null; }
    if (this.audioManager)    { this.audioManager.dispose(); this.audioManager = null; }
    if (this.hud)             { this.hud.dispose(); this.hud = null; }
    if (this.gameHUD)         { this.gameHUD.dispose(); this.gameHUD = null; }
    if (this.commandFeedback) { this.commandFeedback.dispose(); this.commandFeedback = null; }
    if (this.clickPrompt)     { this.clickPrompt.dispose(); this.clickPrompt = null; }
    if (this.outOfBoundsWarning) { this.outOfBoundsWarning.dispose(); this.outOfBoundsWarning = null; }
    if (this.destroyedOverlay) { this.destroyedOverlay.dispose(); this.destroyedOverlay = null; }
    if (this.missionCompleteOverlay) { this.missionCompleteOverlay.dispose(); this.missionCompleteOverlay = null; }
    if (this.trainingMission) { this.trainingMission.dispose(); this.trainingMission = null; }
    if (this.jtacRadio)       { this.jtacRadio.dispose(); this.jtacRadio = null; }
    this._aircraftDestroyed = false;
    this._aircraftTerrainImpact = false;
    this._missionComplete = false;
    this._missionCleared = false;
    this._missionTracking = false;
    this._missionCompleteTimer = -1;
    this._firstRunExitTimer = -1;
    this.missionStats = null;
    if (this.cockpitHUD)      { this.cockpitHUD.dispose(); this.cockpitHUD = null; }
    if (this.cockpitInstruments) { this.cockpitInstruments.dispose(); this.cockpitInstruments = null; }
    if (this.cockpitControls) { this.cockpitControls.dispose(); this.cockpitControls = null; }
    if (this.pilotHead)       { this.pilotHead.dispose(); this.pilotHead = null; }
    if (this.rwrDisplay)      { this.rwrDisplay.dispose(); this.rwrDisplay = null; }
    if (this.forceViz)        { this.forceViz.dispose(); this.forceViz = null; }
    if (this.enemyManager)    { this.enemyManager.dispose(); this.enemyManager = null; }
    if (this.aircraft)        {
      this.scene.remove(this.aircraft.group);
      this.aircraft = null;
    }
    if (this.cameraManager)   { this.cameraManager.dispose(); this.cameraManager = null; }
    this._preview = false;
    this.surfaceAnimator = null;
    this.gearAnimator = null;
    this.groundContact = null;

    // Exit pointer lock if active
    if (document.pointerLockElement) document.exitPointerLock();
  }

  // ── Lighting ────────────────────────────────────────────────────────────────

  applyLighting(key) {
    const cfg = LIGHTING[key];
    if (!cfg) return;
    // A different weather condition brings its own tuned cloud deck, so the
    // start screen's coverage override is dropped (the panel re-seeds its slider
    // to match). Re-applying the same condition keeps it — that's the debug
    // editors' path, and its own cloud editor clears the override explicitly.
    if (key !== this._activeLighting) this._cloudCoverage = null;
    this._activeLighting = key;

    // NVGs reset whenever the lighting condition is (re)applied — the player
    // raises them with N once in the Night condition.
    this.nvgOn = false;
    this._resetNVGFlip();

    // Environment & background
    this.scene.environment = this.assets.getHDR(cfg.hdr);
    this.scene.background = cfg.background
      ? this.assets.getTexture(cfg.background)
      : this.assets.getHDR(cfg.hdr);
    this.scene.environmentIntensity = cfg.environmentIntensity;
    this.renderer.toneMappingExposure = cfg.exposure;

    // Fog
    this.scene.fog.color.setHex(cfg.fog.color);
    this.scene.fog.near = cfg.fog.near;
    this.scene.fog.far = cfg.fog.far;

    // Bloom — strength/params come from the lighting; the enabled flag is
    // reconciled against the user's post-processing toggle by _applyPostFX().
    this.bloomPass.strength = cfg.bloom.strength;
    this.bloomPass.radius = cfg.bloom.radius;
    this.bloomPass.threshold = cfg.bloom.threshold;

    // Contrast / brightness
    if (cfg.contrast) {
      this.contrastPass.uniforms.uContrast.value = cfg.contrast.contrast;
      this.contrastPass.uniforms.uBrightness.value = cfg.contrast.brightness;
    }

    // Film grain
    this.grainPass.uniforms.uStrength.value = cfg.filmGrain.strength;

    // Record the lighting's intended post-FX enablement, then apply it gated by
    // the user's post-processing setting.
    this._fxFromLighting = {
      bloom: cfg.bloom.enabled,
      contrast: !!(cfg.contrast && cfg.contrast.enabled),
      grain: cfg.filmGrain.enabled,
    };
    this._applyPostFX();

    // Sun light
    if (cfg.sun) {
      this.sunLight.visible = cfg.sun.enabled;
      this.sunLight.color.setHex(cfg.sun.color);
      this.sunLight.intensity = cfg.sun.intensity;
      this._sunDirection = new THREE.Vector3(...cfg.sun.direction).normalize();
    } else {
      this.sunLight.visible = false;
    }

    // Ambient light
    if (cfg.ambient) {
      this.ambientLight.visible = true;
      this.ambientLight.color.setHex(cfg.ambient.color);
      this.ambientLight.intensity = cfg.ambient.intensity;
    } else {
      this.ambientLight.visible = false;
    }

    // Terrain grading — the ground's own brightness/tint, separate from the
    // scene-wide environmentIntensity/exposure above (see TERRAIN.grading).
    if (this.terrain) this.terrain.setGrading(cfg.terrain);

    // Star field — self-disables for any condition with no `stars` block, which
    // is every daylight one.
    this.starField?.configure(cfg);

    // Particle + trail lighting
    if (this.particleSystem) {
      const hdr = this.assets.getHDR(cfg.hdr);
      this.particleSystem.setLighting(hdr?.userData.skyTint, cfg.effectBrightness);
    }
    if (this.explosionSystem) {
      const hdr = this.assets.getHDR(cfg.hdr);
      this.explosionSystem.setLighting(hdr?.userData.skyTint, cfg.effectBrightness);
    }
    if (this.smokeTrailSystem) {
      const hdr = this.assets.getHDR(cfg.hdr);
      this.smokeTrailSystem.setLighting(hdr?.userData.skyTint, cfg.effectBrightness);
    }
    if (this.vegetation) {
      const hdr = this.assets.getHDR(cfg.hdr);
      this.vegetation.setLighting(hdr?.userData.skyTint, cfg.effectBrightness);
    }

    // Volumetric cloud deck — coverage/albedo/sun/ambient/fog for this condition.
    // Shares the scenario sun (direction/colour) and the HDR sky tint used above.
    // A player-chosen coverage (start-screen slider) is layered over the
    // condition's own value without touching the config it came from.
    if (this.cloudLayer) {
      const hdr = this.assets.getHDR(cfg.hdr);
      const lcfg = (this._cloudCoverage != null && cfg.clouds)
        ? { ...cfg, clouds: { ...cfg.clouds, coverage: this._cloudCoverage } }
        : cfg;
      this.cloudLayer.configure(lcfg, hdr?.userData.skyTint);
    }

    // Cockpit/instrument self-illumination is driven per-frame by
    // _updateInstrumentBrightness() (engine-state gated), so nothing to set here.
  }

  // Live cloud-coverage override from the start screen's slider. Held here so it
  // survives the scenario rebuild at launch (applyLighting re-applies it), and
  // pushed straight at the deck so a drag previews without the full re-apply.
  setCloudCoverage(coverage) {
    this._cloudCoverage = coverage;
    this.cloudLayer?.setCoverage(coverage);
  }

  // Collect the cockpit/instrument materials whose self-illumination follows the
  // engine power state. Matches both mesh name and material name so it works
  // regardless of how the GLB labels the nodes. Called when the cockpit is built.
  // Collect the transparent cockpit meshes (canopy glass + HUD) that the
  // CockpitOverlayPass redraws over the cloud deck. Glass is matched by the two
  // glass material names (the same pair the shadow opt-out uses); the HUD is the
  // CockpitHUD's mesh. Rebuilt per scenario; the render loop parks these on
  // COCKPIT_OVERLAY_LAYER only while the cockpit camera is active.
  //
  // Everything else solid on the aircraft gets COCKPIT_OCCLUDER_LAYER tagged on
  // (alongside layer 0, so the beauty pass is untouched) for the overlay pass's
  // depth pre-pass — that is what keeps the glass behind the cockpit interior and
  // canopy frame. Tagged once here rather than per frame: no other camera looks at
  // that layer, and the pass only runs in the cockpit view.
  _buildCockpitOverlaySet() {
    this._cockpitOverlayMeshes = [];
    const root = this.aircraft?.group;
    if (!root) return;
    const glassMats = new Set(['M_A10_Glass', 'Cockpit_Glass']);
    root.traverse((child) => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      if (mats.some((m) => m && glassMats.has(m.name))) this._cockpitOverlayMeshes.push(child);
    });
    const hudMesh = this.cockpitHUD?.hudMesh;
    if (hudMesh && !this._cockpitOverlayMeshes.includes(hudMesh)) this._cockpitOverlayMeshes.push(hudMesh);

    root.traverse((child) => {
      if (!child.isMesh || this._cockpitOverlayMeshes.includes(child)) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      // Transparent meshes don't write depth in the beauty pass either, so they
      // must not fake an occluder here.
      if (mats.every((m) => m && m.transparent)) return;
      child.layers.enable(COCKPIT_OCCLUDER_LAYER);
    });
  }

  // Keep the transparent FX — particle sprites and smoke-trail ribbons — on the
  // right side of the cloud deck.
  //
  // Neither writes depth, so the cloud pass — which clips against the depth
  // buffer — composites its deck straight over them. With a deck up, park them on
  // FX_OVERLAY_LAYER: the main RenderPass skips that layer and FXOverlayPass
  // redraws them after the clouds instead. With no deck (a clear condition, or the
  // coverage slider at zero) they go back on layer 0 and render in the beauty pass
  // as before, and the pass turns itself off.
  //
  // Both systems move together — a missile's ribbon and the puffs shedding off it
  // have to end up on the same side of the deck. Their draw order relative to each
  // other is unchanged: every mesh sits at the origin (the geometry carries world
  // positions), so the transparent sort ties on depth and falls through to
  // creation order in the overlay pass exactly as it did in the beauty pass.
  //
  // Run every frame rather than on the cloud-enable edge: the deck flips on and
  // off from applyLighting(), setCloudCoverage() and the debug HUD, and all three
  // mesh sets are rebuilt with each scenario. A few dozen layer writes is cheaper than
  // tracking all of that.
  _syncFXOverlay() {
    if (!this.fxOverlayPass) return;
    const sprites = this.particleSystem?.spriteMeshes;
    const blasts  = this.explosionSystem?.spriteMeshes;
    const ribbons = this.smokeTrailSystem?.ribbonMeshes;
    // Nothing to relocate between scenarios (both are rebuilt per launch) — an
    // enabled pass would blit and re-traverse the scene for an empty layer.
    const active = !!this.cloudLayer?.enabled && !!(sprites || ribbons);
    this.fxOverlayPass.enabled = active;
    const layer = active ? FX_OVERLAY_LAYER : 0;
    if (sprites) for (const m of sprites) m.layers.set(layer);
    if (blasts)  for (const m of blasts)  m.layers.set(layer);
    if (ribbons) for (const m of ribbons) m.layers.set(layer);
  }

  _collectInstrumentMaterials() {
    this._instrumentMaterials = [];
    const node = this.aircraft?.cockpitHD;
    if (!node) return;
    const targets = new Set(['Cockpit_HD', 'instruments']);
    node.traverse((child) => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of mats) {
        if (mat && (targets.has(mat.name) || targets.has(child.name))) {
          this._instrumentMaterials.push(mat);
        }
      }
    });
  }

  // Drive instrument emissive intensity from the engine state: zero when off,
  // the active lighting's instrumentBrightness when running, ramping across the
  // startup/shutdown sequence in step with the HUD power-up (~50% progress).
  _updateInstrumentBrightness() {
    const mats = this._instrumentMaterials;
    if (!mats || !mats.length || !this.aircraft) return;
    const eng = this.aircraft.engineState;
    let power;
    if (eng.isStarting)      power = smoothstep((eng.transitionProgress - 0.4) / 0.25);
    else if (eng.isStopping) power = 1 - smoothstep((eng.transitionProgress - 0.35) / 0.25);
    else                     power = eng.isRunning ? 1 : 0;

    const cfg = LIGHTING[this._activeLighting];
    const intensity = power * (cfg?.instrumentBrightness ?? 1.0);
    for (const mat of mats) mat.emissiveIntensity = intensity;
  }

  // ── Night vision ────────────────────────────────────────────────────────────

  // Toggle the AN/AVS-9 NVGs. Night-only system: pressing N in any other
  // lighting condition is a no-op.
  toggleNVG() {
    if (this._activeLighting !== 'night') return;
    this.nvgOn = !this.nvgOn;
    // Start the flip and keep the pass live even when stowing — the update loop
    // swings the disc back up off-screen and only then disables it.
    this.nvgPass.enabled = true;
    this._nvgAnimating = true;
    this._nvgAnimT = 0;
    // Jolt the cockpit view as the goggles snap down/up.
    this.cameraManager?.cockpitCamera?.kickNVGShake();
  }

  // Advance the goggle flip. Slides the optic-centre uniform from above the top
  // edge (stowed) down to centre (deployed) with an over-shooting ease for snap,
  // plus a fast, exponentially-decaying x/y rattle that rings out as the assembly
  // seats — giving the motion its punch. Cheap: a few scalar ops, no extra passes.
  _updateNVGFlip(dt) {
    if (!this._nvgAnimating) return;

    const MOTION = 0.32; // seconds for the swing itself
    const SETTLE = 0.60; // total window incl. the rattle ringing out
    this._nvgAnimT += dt;
    const t = this._nvgAnimT;

    // Over-shooting ease (ease-out-back): 0 = stowed, 1 = seated. The overshoot
    // dips the disc slightly past centre and back, reading as a downward bounce.
    const p = clamp(t / MOTION, 0, 1);
    const k = 1.70158;
    const u = p - 1;
    const eased = 1 + u * u * ((k + 1) * u + k);
    const seat = this.nvgOn ? eased : 1 - eased;

    // Decaying rattle, faded in over the first frames to avoid a click at t=0.
    const ring  = Math.exp(-t * 9) * clamp(t / 0.05, 0, 1);
    const shakeY = Math.sin(t * 80) * ring * 0.028;
    const shakeX = Math.sin(t * 63 + 1.7) * ring * 0.011;

    const off = this.nvgPass.uniforms.uDiscOffset.value;
    off.x = shakeX;
    off.y = (1 - seat) * NVG_HIDE_Y + shakeY;

    if (t >= SETTLE) {
      this._nvgAnimating = false;
      off.x = 0;
      off.y = this.nvgOn ? 0 : NVG_HIDE_Y;
      // Goggles fully stowed — switch the pass off so it costs nothing.
      if (!this.nvgOn) this.nvgPass.enabled = false;
    }
  }

  // Hard-stow the goggles instantly (no animation) — used when the lighting
  // condition changes or we bail to the start preview.
  _resetNVGFlip() {
    this._nvgAnimating = false;
    this._nvgAnimT = 0;
    if (this.nvgPass) {
      this.nvgPass.enabled = false;
      this.nvgPass.uniforms.uDiscOffset.value.set(0, NVG_HIDE_Y);
    }
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  // Apply a single setting change from the settings overlay, store it in the
  // live settings, and persist it. Every path that changes a setting funnels
  // through here (including the start screen's NO HUD checkbox, routed by
  // UIManager), so this is the one place persistence has to happen.
  applySetting(key, value) {
    saveSetting(key, value); // no-ops on unknown/invalid keys
    switch (key) {
      case 'hud':
        this.settings.hud = value;
        this.gameHUD?.setVisible(value);
        this.commandFeedback?.setVisible(value);
        break;
      case 'mouseSensitivity': this.setMouseSensitivity(value); break;
      case 'controlScheme':
        this.settings.controlScheme = value;
        this._applyControlScheme();
        break;
      case 'invertPitch':
      case 'stickDeadzone':
      case 'joystickAxes':
        this.settings[key] = value;
        this._applyControlOptions();
        break;
      case 'masterVolume':    this.setMasterVolume(value); break;
      case 'musicVolume':     this.setMusicVolume(value); break;
      case 'jtacVolume':      this.setJtacVolume(value); break;
      case 'voiceVolume':     this.setVoiceVolume(value); break;
      case 'renderScale':     this.setRenderScale(value); break;
      case 'postProcessing':
        this.settings.postProcessing = value;
        this._applyPostFX();
        this._applyAA(); // post-FX off forces MSAA on (renderer-AA substitute)
        break;
      case 'antialiasing':
        this.settings.antialiasing = value;
        this._applyAA();
        break;
      case 'shadowResolution': this.setShadowResolution(value); break;
      case 'cloudQuality':    this.setCloudQuality(value); break;
    }
  }

  // Apply a volumetric-cloud quality setting: 'off', or one of the
  // CLOUDS.quality presets (low/medium/high). A preset holds only sampling-cost
  // knobs, so the weather looks the same at every level — it just gets marched
  // more finely. The values are written back into the live CLOUDS block as well
  // as pushed into the running CloudLayer and Terrain, so anything constructed
  // later starts at the selected quality instead of at the config defaults.
  //
  // 'off' is a master switch rather than a level: it disables the pass (and with
  // it the terrain cast), so there is no preset to apply or write back, and the
  // sampling knobs are left as they were for whenever it is switched back on.
  setCloudQuality(name) {
    const q = CLOUDS.quality[name];
    if (!q && name !== 'off') return;
    this.settings.cloudQuality = name;
    this.cloudLayer?.setEnabled(name !== 'off');
    if (!q) return;

    CLOUDS.resolutionScale = q.resolutionScale;
    CLOUDS.blur.sigma      = q.blurSigma;
    CLOUDS.maxSteps        = q.maxSteps;
    CLOUDS.octaves         = q.octaves;
    CLOUDS.shadow.steps    = q.shadowSteps;

    this.cloudLayer?.setQuality(q);
    // No-op when cloud shadows are off — Terrain was built without the patch.
    this.terrain?.setCloudShadowSteps(q.shadowSteps);
  }

  // Reconcile bloom/contrast/grain enablement: the lighting decides whether each
  // effect is wanted, the user's post-processing toggle can switch them all off.
  // RenderPass + OutputPass stay enabled so the scene still renders (through the
  // MSAA target) with correct tone mapping.
  _applyPostFX() {
    const on = this.settings.postProcessing;
    this.bloomPass.enabled    = on && this._fxFromLighting.bloom;
    this.contrastPass.enabled = on && this._fxFromLighting.contrast;
    this.grainPass.enabled    = on && this._fxFromLighting.grain;
    // No lighting term — the film look is one grade for the whole sim, and a
    // strength of 0 skips the pass rather than running a no-op lookup per frame.
    this.filmLookPass.enabled = on && this.settings.filmLook > 0;
  }

  // Apply the MSAA sample count to the composer's render targets:
  //   • 2× render scale → 0 samples. Supersampling already antialiases, so MSAA
  //     on top is redundant and very costly on a 2× HalfFloat target.
  //   • else post-processing on → samples follow the AA setting.
  //   • else (post-processing off) → MSAA forced on, standing in for renderer AA.
  // Changing samples requires reallocating the GL framebuffers, hence dispose().
  _applyAA() {
    let samples;
    if (this.settings.renderScale >= 2) {
      samples = 0;
    } else if (this.settings.postProcessing) {
      samples = this.settings.antialiasing === 'msaa' ? 4 : 0;
    } else {
      samples = 4;
    }
    for (const rt of [this.composer.renderTarget1, this.composer.renderTarget2]) {
      if (rt.samples !== samples) {
        rt.samples = samples;
        rt.dispose();
      }
    }
  }

  setRenderScale(v) {
    this.settings.renderScale = v;
    const ratio = window.devicePixelRatio * v;
    this.renderer.setPixelRatio(ratio);
    this.composer.setPixelRatio(ratio);
    // Star sprites are sized in physical pixels, so they track the ratio.
    this.starField?.setPixelRatio(ratio);
    this.onResize();
    this._applyAA(); // 2× supersampling toggles MSAA off (and back on below 2×)
  }

  /** The scheme in force: the player's choice. */
  get controlScheme() {
    return this.settings.controlScheme;
  }

  // Hand the controller the active scheme. Live mid-flight (the cameras ease
  // between their frames); at launch `snap` puts the cameras straight into it.
  _applyControlScheme({ snap = false } = {}) {
    if (!this.controller) return;
    this.controller.setScheme(this.controlScheme);
    if (snap) {
      this.cameraManager?.chaseCamera.snapFrame();
      this.cameraManager?.cockpitCamera.snapFrame();
    }
  }

  _applyControlOptions() {
    if (!this.controller) return;
    this.controller.invertPitch = this.settings.invertPitch;
    this.controller.deadzone = this.settings.stickDeadzone;
    this.controller.joystickLayout = this.settings.joystickAxes;
  }

  // Gamepad Menu button: the pad's stand-in for Esc / P / R — pause or resume,
  // and the "continue" / "restart" prompts at the end of a run.
  _onPadMenu() {
    if (this.ui.handleEscapeOverlay()) return;
    if (this._firstRunExitTimer >= 0) return;
    if (this.ui.state === 'ended') { this.ui.returnFromEnded(); return; }
    if (this.ui.state === 'paused') { this.resume(); return; }
    if (this.ui.state !== 'playing' || !this.aircraft) return;
    if (this.paused) { this.togglePause(); return; }
    if (this.aircraft.destroyed) { this.restartScenario(); return; }
    if (this._missionComplete) { this._resumeFromMissionComplete(); return; }
    this.pause();
    this.ui.showPause(this._missionCleared);
  }

  setMouseSensitivity(v) {
    this.settings.mouseSensitivity = v;
    if (this.controller) this.controller.sensitivity = AUTOPILOT.sensitivity * v;
  }

  setMasterVolume(v) {
    this.settings.masterVolume = v;
    if (this.audioManager) this.audioManager.setMasterVolume(v);
    this.music?.setMasterVolume(v);
    this.introMusic?.setMasterVolume(v);
    this.trainingMission?.setMasterVolume(v);
    this.jtacRadio?.setMasterVolume(v);
  }

  // Menu/victory music level, on top of the master volume. The MusicPlayer is
  // app-level (it survives scenario teardown), so there's nothing to re-apply
  // on launch the way the per-scenario systems need.
  setMusicVolume(v) {
    this.settings.musicVolume = v;
    this.music?.setMusicVolume(v);
    this.introMusic?.setMusicVolume(v);
  }

  // JTAC radio level, on top of the master volume. Combat-only, and the radio is
  // rebuilt per mission — `_buildGameplaySystems` seeds the new one from
  // `settings`, so this only has to reach a radio that already exists.
  setJtacVolume(v) {
    this.settings.jtacVolume = v;
    this.jtacRadio?.setJtacVolume(v);
  }

  // Flight-school instructor VO level, on top of the master volume. Training-only,
  // and the same story as the JTAC: TrainingMission seeds its CaptionPlayer from
  // `settings`, so this only has to reach a mission already running.
  setVoiceVolume(v) {
    this.settings.voiceVolume = v;
    this.trainingMission?.setVoiceVolume(v);
  }

  // Resize the sunLight shadow map. The existing depth texture must be disposed so
  // Three.js reallocates it at the new resolution on the next render.
  setShadowResolution(v) {
    this.settings.shadowResolution = v;
    this.sunLight.shadow.mapSize.set(v, v);
    if (this.sunLight.shadow.map) {
      this.sunLight.shadow.map.dispose();
      this.sunLight.shadow.map = null;
    }
  }

  // ── Pause / Resume ──────────────────────────────────────────────────────────

  pause() {
    this.paused = true;
    this.timer.setTimescale(0);
    if (document.pointerLockElement) document.exitPointerLock();
  }

  resume() {
    this.paused = false;
    this.timer.setTimescale(1);
    this.ui.enterPlaying();
  }

  togglePause() {
    if (!this.aircraft) return;
    // The first-run completion hold is on its way out to the start screen.
    if (this._firstRunExitTimer >= 0) return;
    if (this.ui.state !== 'playing') return;
    this.paused = !this.paused;
    // Don't touch the clock — keep frame dt flowing so the camera can still
    // follow look input while the sim is frozen.
  }

  _onEscape() {
    // An open overlay closes first, regardless of underlying screen.
    if (this.ui.handleEscapeOverlay()) return;
    // The first-run completion hold is on its way out to the start screen.
    if (this._firstRunExitTimer >= 0) return;

    // A terminal prompt is up (mission cleared, or the A-10 is lost): Esc ends
    // the combat mission and opens the score sheet, ahead of the pause menu.
    if (this.ui.state === 'playing' && this.aircraft && this.missionStats
      && (this._missionComplete || this.aircraft.destroyed)) {
      this.endMission();
      return;
    }

    // While playing the browser releases pointer lock on the first Esc (the
    // keydown isn't delivered to us); the next Esc reaches here and pauses.
    if (this.ui.state === 'playing' && this.aircraft) {
      this.pause();
      this.ui.showPause(this._missionCleared);
    } else if (this.ui.state === 'paused' && this.aircraft) {
      this.resume();
    }
  }

  // End the current combat mission and show the score sheet. Reachable from the
  // mission-complete / aircraft-destroyed prompts (Esc) and the pause menu's
  // "End Mission" button (available once the mission has been cleared). No-op
  // for non-combat scenarios (no MissionStats) and once already on the sheet.
  endMission() {
    if (!this.missionStats || this._exiting || this.ui.state === 'ended') return;
    this.missionStats.finalize();

    // Freeze the sim + release the pointer, and clear any active end-of-run
    // prompt so it doesn't bleed through the score sheet.
    this.pause();
    this.missionCompleteOverlay?.hide();
    this.destroyedOverlay?.hide();

    const outcome = this._missionCleared ? 'complete' : 'failed';

    // Persist the run (score / grade / stats), keyed by mission code. Best score
    // is kept; the start screen badges the mission list from this.
    if (this._scenarioCode) {
      MissionRecords.record(this._scenarioCode, this.missionStats.toResult(outcome));
    }

    // Settle the end-of-mission soundscape so it's identical whether the player
    // arrives straight from the mission-complete prompt (victory theme already
    // swelling, world audio already ducked) or after resuming to free flight and
    // ending from the pause menu (theme stopped, world audio back up).
    //
    // The sim is now paused, so the audio update loop has stopped and a timed
    // master fade would never ramp — snap the scenario soundscape silent.
    if (this.audioManager) this.audioManager.setMasterFade(0);
    // On a cleared mission, bring the victory theme up unless it's already
    // playing (it is when ending straight from the prompt). MusicPlayer's fade
    // runs on its own real-time loop, so it still rises while the sim is paused.
    if (outcome === 'complete' && !this.music.isPlaying) {
      this.music.playFrom(AUDIO.music.victoryStart, AUDIO.music.victoryFadeIn);
    }

    this.ui.showMissionEnded(this.missionStats, outcome);
  }

  // ── Core loop ───────────────────────────────────────────────────────────────

  onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.nvgPass.uniforms.uAspect.value = w / h;
  }

  animate() {
    this.timer.update();
    const dt = Math.min(this.timer.getDelta(), 0.05);

    if (this.airfield) this.airfield.update(this.paused ? 0 : dt, this.camera);
    // The instructor's captions ride sim time and so freeze on their own; his
    // voice is a real-time HTMLAudioElement, so it has to be told (idempotent).
    if (this.trainingMission) this.trainingMission.setPaused(this.paused);
    // Same deal for the JTAC: his cue director runs on sim time, his voice does not.
    if (this.jtacRadio) this.jtacRadio.setPaused(this.paused);
    // World audio freezes with the sim (the AudioContext is suspended, so the
    // loops don't just sit there being mixed inaudibly). Synced here rather than
    // in pause()/resume() because togglePause() (P) flips the flag directly, and
    // this is idempotent.
    if (this.audioManager) this.audioManager.setPaused(this.paused);

    // Devices are read every frame, paused or not, so a gamepad's Menu button
    // can resume; the flight controls themselves only move on sim time.
    if (this.controller && this.ui.state !== 'start') this.controller.poll(dt, this.paused);

    if (!this.paused) {
      this.windManager.update(dt);
      const wind = this.windManager.wind;

      if (this.aircraft)        this.aircraft.syncSceneGraph();
      if (this.controller)      this.controller.update(dt);
      if (this.surfaceAnimator) this.surfaceAnimator.update(this.aircraft, dt);
      if (this.gearAnimator)    this.gearAnimator.update(this.aircraft, dt);
      if (this.groundContact)   this.groundContact.update(this.aircraft, dt, this.terrainCollider);
      if (this.aircraft)        this.aircraft.update(dt, this.groundContact, wind, this.terrainCollider);
      if (this.aircraft && this.aircraft.destroyed && !this._aircraftDestroyed) {
        this._onAircraftDestroyed();
      }
      if (this.aircraft && this.aircraft.terrainImpact && !this._aircraftTerrainImpact) {
        this._onAircraftTerrainImpact();
      }
      if (this.enemyManager)    this.enemyManager.update(dt, this.aircraft);
      // Guided training: advances objectives, drives the objective board and the
      // instructor captions. Ticked on sim time, so it freezes with the pause.
      if (this.trainingMission) this.trainingMission.update(dt);
      // JTAC radio: ticked after the enemies so a kill this frame is already
      // visible to the gun-run call it will prompt.
      if (this.jtacRadio) this.jtacRadio.update(dt);
      // Mission complete once the last enemy is down (combat only), after a short
      // beat of ordinary gameplay (MISSION.completeDelay) so the final kill's
      // explosion and audio land before the victory flow takes the mix. Not while
      // the A-10 is itself destroyed — that one-shot owns the end-of-run flow,
      // and it disarms a delay still running.
      if (this._missionTracking && !this._missionComplete && !this.aircraft?.destroyed
        && this.enemyManager) {
        if (this.enemyManager.aliveCount() > 0) {
          this._missionCompleteTimer = -1;
        } else {
          if (this._missionCompleteTimer < 0) {
            this._missionCompleteTimer = MISSION.completeDelay;
            // The run is won the moment the last enemy dies — only the
            // presentation waits. Latching the outcome here keeps the delay
            // free of gameplay consequences: it closes the stats (so a burst
            // fired into the dirt can't dilute gun accuracy) and leaves a crash
            // during the beat routed through the destroyed flow with the
            // mission still scored as cleared, exactly as a crash after the
            // overlay already was.
            this._missionCleared = !!this.missionStats;
            this.missionStats?.setMissionComplete();
            // Off the net for the duration of the beat, but without cutting a
            // call already going out: idle chatter landing on the last
            // explosion is exactly what the delay exists to protect.
            this.jtacRadio?.standDown();
          }
          this._missionCompleteTimer -= dt;
          if (this._missionCompleteTimer <= 0) this._onMissionComplete();
        }
      }
      // First-run FT-101 complete: after the hold, on to the start screen with
      // the next lesson selected. Pause is locked out while this counts down.
      if (this._firstRunExitTimer >= 0) {
        this._firstRunExitTimer -= dt;
        if (this._firstRunExitTimer < 0) {
          const next = SCENARIOS.findIndex((s) => s.code === INTRO.nextMission);
          if (next >= 0) this.ui.selectScenario(next);
          this.exitToStart();
        }
      }
      if (this.weaponSystem)    this.weaponSystem.update(dt, this.aircraft, this.terrainCollider, wind, this.debug);
      if (this.enemyMissileSystem) {
        this.enemyMissileSystem.update(dt, this.terrainCollider);
        if (this.audioManager) {
          const xs = this.enemyMissileSystem.drainExplosions();
          for (let i = 0; i < xs.length; i++) {
            const e = xs[i];
            if (e.isAircraftHit) {
              // Cockpit gets the muffled interior boom; external views keep the
              // normal blast. The master-caution loop is driven separately by
              // AudioManager off the aircraft's engine-damage state.
              if (!this.audioManager.playInteriorExplosion('Explosion_AIM9_Interior')) {
                this.audioManager.playExplosion(e.name, e.x, e.y, e.z);
              }
            } else {
              this.audioManager.playExplosion(e.name, e.x, e.y, e.z);
            }
          }
          // Launches are drained inside AudioManager._updateRWR for the
          // RWR launch warning — no further pass needed here.
        } else {
          this.enemyMissileSystem.drainExplosions();
          this.enemyMissileSystem.drainLaunches();
        }
      }

      // Drain weapon events → spawn particles
      if (this.weaponSystem && this.particleSystem) {
        const impacts = this.weaponSystem.drainImpacts();
        for (let i = 0; i < impacts.length; i++) {
          const imp = impacts[i];
          this.particleSystem.spawnIncendiaryImpact(imp.x, imp.y, imp.z);
          this.particleSystem.spawnShrapnel(imp.x, imp.y, imp.z);
          if (imp.type === 'terrain') {
            this.particleSystem.spawnDirtImpact(imp.x, imp.y, imp.z);
          }
        }
        const flashes = this.weaponSystem.drainMuzzleFlashes();
        for (let i = 0; i < flashes.length; i++) {
          const f = flashes[i];
          this.particleSystem.spawnMuzzleFlash(
            f.x, f.y, f.z,
            f.vx, f.vy, f.vz,
            f.dx, f.dy, f.dz,
          );
        }
      }
      if (this.flareSystem)     this.flareSystem.update(dt, wind);
      if (this.aircraftShadow)  this.aircraftShadow.update(this.aircraft, this.terrainCollider);
      if (this.particleSystem)  this.particleSystem.update(dt, this.camera, wind);
      if (this.explosionSystem) this.explosionSystem.update(dt);
      if (this.smokeTrailSystem) this.smokeTrailSystem.update(dt, this.camera, wind);
      if (this.audioManager)    this.audioManager.update(dt, wind.length());
      if (this.forceViz)        this.forceViz.update(this.aircraft, this.groundContact, wind);

      // Out-of-bounds grace timer + forced return-to-combat.
      this._updateOutOfBounds(dt);
    }

    // Keep shadow frustum centered on the followed subject — the A-10 normally,
    // or the selected enemy while in enemy-camera mode — tightening onto the
    // cockpit in cockpit mode.
    if (this.aircraft && this._sunDirection) {
      const cm = this.cameraManager;

      // In enemy-camera mode the shadow tracks the framed enemy instead of the
      // A-10. Same default frustum as the chase view (cockpit blend stays 0).
      const enemyMode = cm && (cm.mode === CameraMode.ENEMY
        || (cm._transitioning && cm._toMode === CameraMode.ENEMY));
      const enemyPos = enemyMode ? cm.enemyCamera?._entityPosition() : null;

      // Tighten onto the cockpit whenever the view is inside it — the cockpit
      // mode or an action mount in the canopy — eased along the transition.
      this._shadowCockpitBlend = cm ? cm.interiorBlend : 0;

      const targetPos = this.sunLight.target.position;
      if (enemyPos) {
        targetPos.copy(enemyPos);
      } else {
        // Blend shadow target between aircraft origin and FPV node
        const fpvNode = this.cameraManager?.cockpitCamera?.fpvNode;
        targetPos.copy(this.aircraft.position);
        if (fpvNode && this._shadowCockpitBlend > 0) {
          fpvNode.getWorldPosition(_fpvWorld);
          targetPos.lerp(_fpvWorld, this._shadowCockpitBlend);
        }
      }

      this.sunLight.position.copy(targetPos).addScaledVector(this._sunDirection, -20);

      // Interpolate shadow frustum params
      const sh = RENDERING.shadow;
      const shC = RENDERING.shadowCockpit;
      const b = this._shadowCockpitBlend;
      const frustum = sh.frustumSize + (shC.frustumSize - sh.frustumSize) * b;
      const near = sh.near + (shC.near - sh.near) * b;
      const far = sh.far + (shC.far - sh.far) * b;
      const cam = this.sunLight.shadow.camera;
      cam.left = -frustum;
      cam.right = frustum;
      cam.top = frustum;
      cam.bottom = -frustum;
      cam.near = near;
      cam.far = far;
      cam.updateProjectionMatrix();
    }
    // Maverick seeker — updates the gimballed LOS, TV feed state and aim flags
    // before the camera manager ticks so the cockpit aim pose reads fresh state.
    if (this.maverickSeeker) this.maverickSeeker.update(dt);

    // First-run intro: types the card and drives the black between shots. Ahead
    // of the camera so the frame that reveals the picture also starts the shot.
    if (this.intro) {
      this.intro.update(dt);
      if (this.intro.done) this.intro = null;
    }

    if (this.cameraManager) {
      this.cameraManager.update(dt);

      // Toggle cockpit LOD + the seated pilot. Keyed off the camera's dressing
      // rather than the raw mode: mid-transition it defers the swap until the
      // camera is at the canopy, so the models change over while the frame is
      // full of airframe, and an action mount names what it wants drawn. That
      // reads this frame's camera pose, hence after update().
      const dressing = this.cameraManager.dressing;
      const cockpit = dressing.interior;
      if (this.aircraft.cockpitHD) this.aircraft.cockpitHD.visible = cockpit;
      if (this.aircraft.cockpitSD) this.aircraft.cockpitSD.visible = !cockpit;
      // The pilot is a top-level node (the action cameras frame him from inside
      // the canopy, where the SD shell he used to hang off isn't drawn), so his
      // visibility is ours to set rather than inherited.
      if (this.aircraft.pilot) this.aircraft.pilot.visible = dressing.pilot;

      // In the cockpit view, park the transparent cockpit (canopy glass + HUD) on
      // the overlay layer so the main RenderPass — and therefore the cloud pass —
      // skips it, and enable CockpitOverlayPass to redraw it over the deck. In any
      // other view the meshes stay on layer 0 and render normally with the aircraft.
      const overlayActive = cockpit && this._cockpitOverlayMeshes.length > 0;
      if (this.cockpitOverlayPass) this.cockpitOverlayPass.enabled = overlayActive;
      const meshLayer = overlayActive ? COCKPIT_OVERLAY_LAYER : 0;
      for (const m of this._cockpitOverlayMeshes) m.layers.set(meshLayer);
    }
    if (this.terrain)         this.terrain.update(this.camera, dt, this.aircraft?.position);
    if (this.vegetation)      this.vegetation.update(this.camera);
    if (this.hud && this.debug) this.hud.update();
    if (this.gameHUD)     this.gameHUD.update(dt);
    if (this.commandFeedback) this.commandFeedback.update();
    if (this.clickPrompt) this.clickPrompt.update(this.ui.state === 'playing' && !this.controller.isLocked
      && this.controller.needsPointerLock && !this.aircraft?.destroyed);
    if (this.cockpitHUD)  this.cockpitHUD.update(dt);
    this._updateInstrumentBrightness();
    if (this.cockpitInstruments) this.cockpitInstruments.update(dt);
    if (this.cockpitControls) this.cockpitControls.update(dt);
    if (this.pilotHead)   this.pilotHead.update(dt);
    if (this.rwrDisplay)  this.rwrDisplay.update(dt);

    this.grainPass.uniforms.uTime.value += dt;
    // Drift the cloud deck. dt is 0 while paused (timescale 0), so it freezes
    // with the sim — matching the film-grain clock above.
    if (this.cloudLayer) this.cloudLayer.update(dt);
    // Rides with the camera and clocks the scintillation — also frozen on pause.
    if (this.starField) this.starField.update(dt);
    if (this.nvgPass.enabled) {
      this.nvgPass.uniforms.uTime.value += dt;
      // Match the goggle disc to the cockpit zoom: magnification is the ratio of
      // the base cockpit FOV to the live (smoothly-lerped) FOV, so it eases in
      // and out with the right-click zoom. Only the cockpit view zooms, so any
      // other camera mode leaves the optic at its native size.
      const cm = this.cameraManager;
      const inCockpit = cm && (cm.mode === CameraMode.COCKPIT
        || (cm._transitioning && cm._toMode === CameraMode.COCKPIT));
      let zoom = 1;
      if (inCockpit) {
        const half = THREE.MathUtils.degToRad(this.camera.fov * 0.5);
        const baseHalf = THREE.MathUtils.degToRad(CAMERA.cockpit.fov * 0.5);
        zoom = Math.max(1, Math.tan(baseHalf) / Math.tan(half));
      }
      this.nvgPass.uniforms.uZoom.value = zoom;
      this._updateNVGFlip(dt);
    }

    this._syncFXOverlay();

    this.renderer.info.reset();

    // Render the Maverick TV feed into its offscreen targets before the main
    // composited frame samples them on the cockpit `_TV` plane. No-op unless the
    // Maverick is selected (with a round mounted) and the cockpit view is active.
    if (this.maverickSeeker) this.maverickSeeker.renderTV(this.renderer);

    this.composer.render();
  }
}
